import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, RefreshCw, Trash2, Search, X, LogOut, Eye, TrendingUp, ExternalLink, Edit, CreditCard, Bell, Crown, ChevronRight, ChevronDown, ShoppingBag, Package, Tag, BarChart2, Warehouse, Settings, Inbox, Check, Clock, MoreHorizontal, UserCog, AlertTriangle, Lock, Menu, Mail, User as UserIcon } from 'lucide-react';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import apiClient, { API_BASE_URL } from '../api/apiClient';
import { AlertModal } from '../components/AlertModal';
import { EditAlertView } from './EditAlertView';
import { Alert, AlertFormData, User, SearchResult, PriceAnalysis } from '../types/alert.types';
import { buildFinalVintedUrl } from '../hooks/useAlertForm';
import { getAvailableFilters } from '../utils/categoryFilters';
import CrmAchats from './crm/CrmAchats';
import CrmReception from './crm/CrmReception';
import CrmStock from './crm/CrmStock';
import CrmVentes from './crm/CrmVentes';
import CrmComptabilite from './crm/CrmComptabilite';
import CrmConfiguration from './crm/CrmConfiguration';
import { AccountSettings } from './AccountSettings';

const LAUNCH_END_APP = new Date('2026-05-31T23:59:59');
function getLaunchDaysLeft() {
  return Math.max(0, Math.ceil((LAUNCH_END_APP.getTime() - Date.now()) / 86400000));
}

// Custom Modal Component (Style inspiré de Souk)
interface CustomModalProps {
  show: boolean;
  title: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'confirm';
  onClose: () => void;
  actionButton?: {
    text: string;
    onClick: () => void;
    variant?: 'danger' | 'primary';
  };
}

const CustomModal: React.FC<CustomModalProps> = ({ show, title, message, type, onClose, actionButton }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[101] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full animate-slideIn">
        <div className="px-6 pt-6 pb-2 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {title}
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            {message}
          </p>
        </div>

        <div className={`flex gap-3 px-6 pb-5 pt-3 ${actionButton ? 'justify-center' : 'justify-center'}`}>
          {actionButton && (
            <button
              onClick={actionButton.onClick}
              className={`px-6 py-2.5 rounded-full text-white font-semibold transition-all hover:scale-105 shadow-lg text-sm ${
                actionButton.variant === 'danger'
                  ? 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700'
                  : 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600'
              }`}
            >
              {actionButton.text}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all font-semibold hover:scale-105 text-sm"
          >
            {actionButton ? 'Annuler' : 'OK'}
          </button>
        </div>
      </div>

      {/* Styles d'animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.15s ease-out;
        }

        .animate-slideIn {
          animation: slideIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

const VintedPriceTracker: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Auth & User
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');

  // Navigation
  const [currentPage, setCurrentPage] = useState<'alerts' | 'subscription' | 'edit-alert' | 'optimize' | 'crm-achats' | 'crm-reception' | 'crm-stock' | 'crm-ventes' | 'crm-comptabilite' | 'crm-configuration' | 'account-settings' | 'contact'>(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const validPages = ['alerts', 'subscription', 'optimize', 'crm-achats', 'crm-reception', 'crm-stock', 'crm-ventes', 'crm-comptabilite', 'crm-configuration', 'account-settings', 'contact'];
    return (validPages.includes(page || '') ? page : 'alerts') as any;
  });

  // Abonnement — toggle mensuel/annuel + factures + ref scroll
  const [subBillingAnnual, setSubBillingAnnual] = useState(false);
  const plansRef = useRef<HTMLDivElement>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [contactEmailCopied, setContactEmailCopied] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState<{ reason: 'starter' | 'hunter_exhausted' } | null>(null);
  const hasAutoRouted = useRef(false);

  // Stock — livraisons ambiguës en attente
  const [stockPendingCount, setStockPendingCount] = useState(0);
  const fetchStockPendingCount = React.useCallback(async () => {
    try {
      const r = await apiClient.get('/crm/stock/pending-receive');
      setStockPendingCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch { /* silencieux */ }
  }, []);

  // Ventes — conflits de synchronisation en attente
  const [salesConflictCount, setSalesConflictCount] = useState(0);
  const fetchSalesConflictCount = React.useCallback(async () => {
    try {
      const r = await apiClient.get('/crm/sales/label-conflicts');
      setSalesConflictCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch { /* silencieux */ }
  }, []);

  // Alerts
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingAlerts, setRefreshingAlerts] = useState<Record<number, { progress: number }>>({});
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [showDeleteMultipleModal, setShowDeleteMultipleModal] = useState(false);

  // Results & Analysis
  const [selectedAlertResults, setSelectedAlertResults] = useState<SearchResult[]>([]);
  const [selectedAlertAnalysis, setSelectedAlertAnalysis] = useState<PriceAnalysis | null>(null);
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(null);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);
  const [showSoldItemsModal, setShowSoldItemsModal] = useState(false);
  const [soldItems, setSoldItems] = useState<any[]>([]);
  const [soldItemsLoading, setSoldItemsLoading] = useState(false);
  const [soldItemsAlertName, setSoldItemsAlertName] = useState('');
  // Historique des scans par alerte
  const [scanHistories, setScanHistories] = useState<Record<number, any[]>>({});
  const [scanHistoriesLoading, setScanHistoriesLoading] = useState(false);

  // Search & UI
  const [searchQuery, setSearchQuery] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [customModal, setCustomModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    actionButton?: { text: string; onClick: () => void };
  }>({ show: false, title: '', message: '', type: 'info' });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [activeFilters, setActiveFilters] = useState<Array<{ field: keyof SearchResult; value: string }>>([]);
  const [selectedAlerts, setSelectedAlerts] = useState<Set<number>>(new Set());
  const [expandedAlertFilters, setExpandedAlertFilters] = useState<Record<string, boolean>>({});

  // États de tri des résultats
  const [sortBy, setSortBy] = useState<'likes' | 'price'>('likes');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc'); // desc = du plus grand au plus petit

  // Fonction pour recalculer l'analyse des prix côté client
  const calculateAnalysis = (items: SearchResult[]): PriceAnalysis => {
    if (items.length === 0) return {
      overall_stats: { min: 0, max: 0, avg: 0, median: 0 },
      condition_impact: {},
      color_impact: {},
      size_impact: {},
      brand_impact: {},
      category_impact: {},
      total_items_analyzed: 0
    };

    const prices = items.map(item => item.price).filter(p => p > 0);
    const avg = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
    const sortedPrices = [...prices].sort((a, b) => a - b);
    const median = prices.length > 0 ? sortedPrices[Math.floor(sortedPrices.length / 2)] : 0;

    const getImpact = (field: keyof SearchResult) => {
      const impact: Record<string, { avg_price: number; count: number }> = {};
      items.forEach(item => {
        const val = item[field];
        if (val && typeof val === 'string' && item.price > 0) {
          // Normaliser en minuscules pour éviter les problèmes de casse
          const valLower = val.toLowerCase().trim();
          
          // Gérer les couleurs multiples (ex: "Noir et Blanc" ou "noir et blanc")
          if (field === 'color' && valLower.includes(' et ')) {
            // Décomposer et ajouter pour chaque couleur (en minuscules)
            const colors = valLower.split(' et ').map(c => c.trim());
            colors.forEach(color => {
              if (!impact[color]) impact[color] = { avg_price: 0, count: 0 };
              impact[color].avg_price += item.price;
              impact[color].count += 1;
            });
          } else {
            // Comportement normal (toujours en minuscules)
            if (!impact[valLower]) impact[valLower] = { avg_price: 0, count: 0 };
            impact[valLower].avg_price += item.price;
            impact[valLower].count += 1;
          }
        }
      });
      Object.keys(impact).forEach(key => {
        impact[key].avg_price = impact[key].avg_price / impact[key].count;
      });
      return impact;
    };

    return {
      overall_stats: {
        min: prices.length > 0 ? Math.min(...prices) : 0,
        max: prices.length > 0 ? Math.max(...prices) : 0,
        avg: avg,
        median: median
      },
      condition_impact: getImpact('condition'),
      color_impact: getImpact('color'),
      size_impact: getImpact('size'),
      brand_impact: getImpact('brand'),
      category_impact: getImpact('category_name'),
      total_items_analyzed: items.length
    };
  };

  const showNotification = (
    title: string,
    message: string,
    type: 'success' | 'error' | 'warning' | 'info',
    actionButton?: { text: string; onClick: () => void }
  ) => {
    setCustomModal({ show: true, title, message, type, actionButton });
  };

  const isRefreshing = refreshingAll || Object.keys(refreshingAlerts).length > 0;

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUserData();
      fetchStockPendingCount();
      fetchSalesConflictCount();
    }
  }, [fetchStockPendingCount, fetchSalesConflictCount]);

  // Retour depuis Stripe Checkout — rafraîchir le plan et notifier
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('sub_success') === '1') {
      fetchUserData();
      setCurrentPage('subscription');
      showNotification('Abonnement activé !', 'Votre plan a bien été mis à jour. Bienvenue !', 'success');
      // Nettoyer le paramètre URL
      const cleanUrl = window.location.pathname + '?page=subscription';
      window.history.replaceState({}, '', cleanUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les factures quand on navigue vers la page abonnement
  useEffect(() => {
    if (currentPage === 'subscription' && isAuthenticated) {
      fetchInvoices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isAuthenticated]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isRefreshing) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRefreshing]);

  // Réinitialiser le tri à "likes desc" quand on ouvre les résultats
  useEffect(() => {
    if (showResultsModal && selectedAlertResults.length > 0) {
      setSortBy('likes');
      setSortOrder('desc');
    }
  }, [showResultsModal]);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const { data } = await apiClient.get('/payments/invoices');
      setInvoices(Array.isArray(data) ? data : []);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const determineInitialPage = (userData: User) => {
    const params = new URLSearchParams(location.search);
    if (params.get('page')) return;
    if (userData.plan === 'hunter' || userData.plan === 'business' || userData.is_unlimited) {
      setCurrentPage('alerts');
    } else {
      setCurrentPage('crm-achats');
    }
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAuthenticated(true);
        loadAlerts();
        loadScanHistories();
        if (!hasAutoRouted.current) {
          hasAutoRouted.current = true;
          determineInitialPage(userData);
        }
      } else {
        localStorage.removeItem('access_token');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsAuthenticated(false);
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const idToken = await result.user.getIdToken();
      const { user: userData, is_new_user } = await apiClient.loginWithGoogle(idToken);
      setUser(userData);
      setIsAuthenticated(true);
      hasAutoRouted.current = true;
      await loadAlerts();
      loadScanHistories();
      if (is_new_user) {
        setCurrentPage('crm-configuration');
      } else {
        determineInitialPage(userData);
      }
    } catch (error: any) {
      setAuthError(error.message || 'Erreur de connexion avec Google');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setUser(null);
    setIsAuthenticated(false);
    setAlerts([]);
    setScanHistories({});
  };

  const loadAlerts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/alerts/?_t=${Date.now()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Cache-Control': 'no-cache' }
      });
      if (response.ok) {
        const data = await response.json();
        // Convertir price_range_min/max en price_range pour compatibilité
        const alertsWithPriceRange = data.map((alert: any) => ({
          ...alert,
          price_range: (alert.price_range_min != null && alert.price_range_max != null)
            ? { min: alert.price_range_min, max: alert.price_range_max }
            : undefined
        }));
        setAlerts(alertsWithPriceRange);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const handleCreateAlert = async (formData: AlertFormData) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const isEditing = editingAlert !== null;
      const vintedUrl = buildFinalVintedUrl(formData);

      const alertData: any = {
        name: formData.name,
        vinted_url: vintedUrl,
        max_pages: parseInt(formData.maxPages, 10) || 5,
        trending_only: formData.trendingOnly,
        must_include: formData.mustInclude,
        must_exclude: formData.mustExclude,
        category_id: formData.categoryId || null,
        category_path: formData.categoryPath || null,
        selected_conditions: formData.selectedConditions,
        selected_sizes: formData.selectedSizes,
        selected_colors: formData.selectedColors,
        selected_brands: formData.selectedBrands,
        selected_statuses: formData.selectedStatuses,
        selected_platforms: formData.selectedPlatforms || [],
        selected_ratings: formData.selectedRatings || [],
        selected_materials: formData.selectedMaterials || [],
        selected_patterns: formData.selectedPatterns || [],
        selected_languages: formData.selectedLanguages || []
      };

      // Gérer les prix : toujours inclure les valeurs lors de l'édition
      if (isEditing) {
        // En mode édition, on envoie toujours les valeurs (même si vides) pour que le backend les mette à jour
        if (formData.minPrice && formData.minPrice.trim() !== '') {
          const minPriceValue = parseFloat(formData.minPrice);
          if (!isNaN(minPriceValue)) {
            alertData.min_price = minPriceValue;
          } else {
            alertData.min_price = null;
          }
        } else {
          alertData.min_price = null;
        }

        if (formData.maxPrice && formData.maxPrice.trim() !== '') {
          const maxPriceValue = parseFloat(formData.maxPrice);
          if (!isNaN(maxPriceValue)) {
            alertData.max_price = maxPriceValue;
          } else {
            alertData.max_price = null;
          }
        } else {
          alertData.max_price = null;
        }
      } else {
        // En mode création, on inclut seulement si la valeur n'est pas vide
        if (formData.minPrice && formData.minPrice.trim() !== '') {
          const minPriceValue = parseFloat(formData.minPrice);
          if (!isNaN(minPriceValue)) {
            alertData.min_price = minPriceValue;
          }
        }

        if (formData.maxPrice && formData.maxPrice.trim() !== '') {
          const maxPriceValue = parseFloat(formData.maxPrice);
          if (!isNaN(maxPriceValue)) {
            alertData.max_price = maxPriceValue;
          }
        }
      }

      // Gérer les likes : toujours inclure les valeurs lors de l'édition
      if (isEditing) {
        // En mode édition, on envoie toujours les valeurs (même si vides) pour que le backend les mette à jour
        if (formData.minLikes && formData.minLikes.trim() !== '') {
          const minLikesValue = parseInt(formData.minLikes);
          if (!isNaN(minLikesValue)) {
            alertData.min_likes = minLikesValue;
          } else {
            alertData.min_likes = null;
          }
        } else {
          alertData.min_likes = null;
        }

        if (formData.maxLikes && formData.maxLikes.trim() !== '') {
          const maxLikesValue = parseInt(formData.maxLikes);
          if (!isNaN(maxLikesValue)) {
            alertData.max_likes = maxLikesValue;
          } else {
            alertData.max_likes = null;
          }
        } else {
          alertData.max_likes = null;
        }

        // Gérer les filtres : toujours inclure les valeurs lors de l'édition
        alertData.selected_brands = formData.selectedBrands || [];
        alertData.selected_statuses = formData.selectedStatuses || [];
        alertData.selected_platforms = formData.selectedPlatforms || [];
        alertData.selected_ratings = formData.selectedRatings || [];
        alertData.selected_materials = formData.selectedMaterials || [];
        alertData.selected_patterns = formData.selectedPatterns || [];
        alertData.selected_languages = formData.selectedLanguages || [];
      } else {
        // En mode création, on inclut seulement si la valeur n'est pas vide
        if (formData.minLikes && formData.minLikes.trim() !== '') {
          const minLikesValue = parseInt(formData.minLikes);
          if (!isNaN(minLikesValue)) {
            alertData.min_likes = minLikesValue;
          }
        }

        if (formData.maxLikes && formData.maxLikes.trim() !== '') {
          const maxLikesValue = parseInt(formData.maxLikes);
          if (!isNaN(maxLikesValue)) {
            alertData.max_likes = maxLikesValue;
          }
        }
      }
      const url = isEditing ? `${API_BASE_URL}/alerts/${editingAlert.id}` : `${API_BASE_URL}/alerts/`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(alertData)
      });

      if (response.ok) {
        const updatedData = await response.json();

        if (isEditing && editingAlert) {
          // En mode édition : mettre à jour l'alerte dans le state en préservant les stats calculées
          // (last_avg_price, sold_price_*, etc.) qui ne sont pas modifiées par le PUT
          setAlerts(prev => prev.map(a => {
            if (a.id !== editingAlert.id) return a;
            return {
              ...a,            // préserve tous les champs existants (dont last_avg_price)
              ...updatedData,  // écrase avec les nouvelles valeurs du backend
              // Sécurité : si l'API renvoie null pour les stats calculées, garder l'ancienne valeur
              last_avg_price: updatedData.last_avg_price ?? a.last_avg_price,
              sold_price_avg: updatedData.sold_price_avg ?? a.sold_price_avg,
              sold_price_min: updatedData.sold_price_min ?? a.sold_price_min,
              sold_price_max: updatedData.sold_price_max ?? a.sold_price_max,
              sold_count: updatedData.sold_count ?? a.sold_count,
              price_range: (updatedData.price_range_min != null && updatedData.price_range_max != null)
                ? { min: updatedData.price_range_min, max: updatedData.price_range_max }
                : a.price_range,
            };
          }));
        } else {
          // En mode création : re-fetcher toutes les alertes (nouvel ID inconnu)
          await loadAlerts();
          loadScanHistories(); // aussi rafraîchir l'historique pour la fourchette "vs précédent"
        }

        setShowModal(false);
        setEditingAlert(null);
        setCurrentPage('alerts');
        showNotification(
          isEditing ? 'Alerte modifiée' : 'Alerte créée',
          isEditing ? 'Votre alerte a été modifiée avec succès' : 'Votre alerte a été créée avec succès',
          'success'
        );
      } else {
        const error = await response.json();
        
        // Gérer les erreurs de validation Pydantic (qui peuvent être des tableaux)
        let errorMessage = 'Erreur lors de la création';
        if (error.detail) {
          if (Array.isArray(error.detail)) {
            // Erreurs de validation Pydantic : format [{loc: [...], msg: "...", type: "..."}, ...]
            errorMessage = error.detail
              .map((err: any) => {
                const field = Array.isArray(err.loc) ? err.loc.slice(1).join('.') : err.loc;
                return `${field}: ${err.msg}`;
              })
              .join(', ');
          } else if (typeof error.detail === 'string') {
            errorMessage = error.detail;
          } else {
            errorMessage = JSON.stringify(error.detail);
          }
        }
        
        showNotification('Erreur', errorMessage, 'error');
      }
    } catch (error) {
      showNotification('Erreur', 'Erreur de connexion au serveur', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAlert = async (alert: Alert) => {
    if (isRefreshing) return;
    setEditingAlert(alert);
    setCurrentPage('edit-alert');
    const token = localStorage.getItem('access_token');
    try {
      const response = await fetch(`${API_BASE_URL}/alerts/${alert.id}/results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const results = data.results || [];
        setSelectedAlertResults(results);
        setSelectedAlertAnalysis(data.analysis || calculateAnalysis(results));
      }
    } catch (e) {
      console.error("Erreur chargement résultats pré-édition", e);
    }
  };

  const handleBackToAlerts = () => {
    setCurrentPage('alerts');
    setEditingAlert(null);
    setSelectedAlertResults([]);
  };

  const handleDeleteAlert = (alert: Alert) => {
    if (isRefreshing) return;
    setAlertToDelete(alert);
    setShowDeleteModal(true);
  };

  const confirmDeleteAlert = async () => {
    if (!alertToDelete) return;
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/alerts/${alertToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await loadAlerts();
        showNotification('Alerte supprimée', 'L\'alerte a été supprimée avec succès', 'success');
        setShowDeleteModal(false);
        setAlertToDelete(null);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
        showNotification('Erreur', errorData.detail || 'Impossible de supprimer l\'alerte', 'error');
      }
    } catch (error) {
      showNotification('Erreur', 'Erreur de connexion au serveur', 'error');
    }
  };

  const confirmDeleteMultipleAlerts = async () => {
    try {
      const token = localStorage.getItem('access_token');
      await Promise.all(
        Array.from(selectedAlerts).map(id =>
          fetch(`${API_BASE_URL}/alerts/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
      await loadAlerts();
      setSelectedAlerts(new Set());
      setShowDeleteMultipleModal(false);
      showNotification('Succès', `${selectedAlerts.size} alerte${selectedAlerts.size > 1 ? 's supprimées' : ' supprimée'}`, 'success');
    } catch (error) {
      showNotification('Erreur', 'Erreur lors de la suppression', 'error');
    }
  };

  const refreshUserSearchCount = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const r = await fetch(`${API_BASE_URL}/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) {
        const fresh = await r.json();
        setUser(prev => prev ? { ...prev, monthly_searches_used: fresh.monthly_searches_used } : prev);
        if (fresh.plan === 'hunter' && !fresh.is_unlimited && (fresh.monthly_searches_used || 0) >= 25) {
          setShowUpgrade({ reason: 'hunter_exhausted' });
        }
      }
    } catch {}
  };

  const handleRefreshAlert = async (alertId: number, silent = false): Promise<boolean> => {
    const alert = alerts.find(a => a.id === alertId);
    if (!alert) return false;
    setRefreshingAlerts(prev => ({ ...prev, [alertId]: { progress: 0 } }));
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/alerts/${alertId}/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let gotAlertFromSSE = false;
        let gotEmptyResults = false;
        let buffer = '';
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const messages = buffer.split('\n\n');
            buffer = messages.pop() || '';
            for (const msg of messages) {
              const line = msg.split('\n').find((l: string) => l.startsWith('data: '));
              if (line) {
                try {
                  const data = JSON.parse(line.slice(6));
                  if (data.progress !== undefined) {
                    setRefreshingAlerts(prev => ({ ...prev, [alertId]: { progress: data.progress } }));
                  }
                  if (data.warning === 'empty_results') {
                    // 403 Cloudflare ou résultat vide — les stats de l'alerte sont préservées
                    gotAlertFromSSE = true; // on a reçu une réponse, ne pas recharger depuis DB
                    gotEmptyResults = true;
                    if (!silent) showNotification('Aucun résultat', 'Vinted n\'a retourné aucun article (blocage temporaire Cloudflare). Réessaie dans quelques minutes.', 'warning');
                  }
                  if (data.alert) {
                    gotAlertFromSSE = true;
                    const upd = data.alert;
                    setAlerts(prev => prev.map(a => a.id === alertId ? {
                      ...a,
                      total_results: upd.total_results,
                      last_scanned: upd.last_scanned,
                      price_range_min: upd.price_range_min,
                      price_range_max: upd.price_range_max,
                      last_avg_price: upd.last_avg_price,
                      prev_avg_price: upd.prev_avg_price ?? a.prev_avg_price,
                      price_range: (upd.price_range_min != null && upd.price_range_max != null) ? { min: upd.price_range_min, max: upd.price_range_max } : a.price_range,
                      sold_price_avg: upd.sold_price_avg,
                      sold_price_min: upd.sold_price_min,
                      sold_price_max: upd.sold_price_max,
                      sold_count: upd.sold_count
                    } : a));
                  }
                } catch (e) { console.error('Error parsing SSE:', e); }
              }
            }
          }
          if (buffer) {
            const line = buffer.split('\n').find((l: string) => l.startsWith('data: '));
            if (line) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.progress !== undefined) setRefreshingAlerts(prev => ({ ...prev, [alertId]: { progress: data.progress } }));
                if (data.warning === 'empty_results') {
                  gotAlertFromSSE = true;
                  gotEmptyResults = true;
                  if (!silent) showNotification('Aucun résultat', 'Vinted n\'a retourné aucun article (blocage temporaire Cloudflare). Réessaie dans quelques minutes.', 'warning');
                }
                if (data.alert) {
                  gotAlertFromSSE = true;
                  const upd = data.alert;
                  setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, total_results: upd.total_results, last_scanned: upd.last_scanned, price_range_min: upd.price_range_min, price_range_max: upd.price_range_max, last_avg_price: upd.last_avg_price, prev_avg_price: upd.prev_avg_price ?? a.prev_avg_price, price_range: (upd.price_range_min != null && upd.price_range_max != null) ? { min: upd.price_range_min, max: upd.price_range_max } : a.price_range, sold_price_avg: upd.sold_price_avg, sold_price_min: upd.sold_price_min, sold_price_max: upd.sold_price_max, sold_count: upd.sold_count } : a));
                }
              } catch (e) { /* dernier chunk partiel */ }
            }
          }
        }
        // Ne pas appeler loadAlerts si on a déjà les données via SSE (évite d'écraser les stats de l'extract)
        if (!gotAlertFromSSE) await loadAlerts();
        // Rafraîchir l'historique des scans après chaque scan
        loadScanHistories();
        refreshUserSearchCount();
        setRefreshingAlerts(prev => {
          const newState = { ...prev };
          delete newState[alertId];
          return newState;
        });
        if (!silent && !gotEmptyResults) showNotification('Recherche terminée', 'Les résultats ont été mis à jour', 'success');
        return true;
      } else {
        const errData = await response.json().catch(() => ({}));
        if (!silent) showNotification('Erreur', errData.detail || 'Erreur lors du rafraîchissement', 'error');
        setRefreshingAlerts(prev => {
          const newState = { ...prev };
          delete newState[alertId];
          return newState;
        });
        return false;
      }
    } catch (error) {
      setRefreshingAlerts(prev => {
        const newState = { ...prev };
        delete newState[alertId];
        return newState;
      });
      if (!silent) showNotification('Erreur', 'Erreur lors du rafraîchissement', 'error');
      return false;
    }
  };

  const handleRefreshSelectedAlerts = async () => {
    const ids = Array.from(selectedAlerts);
    if (ids.length === 0) return;
    setRefreshingAll(true);
    let completedCount = 0;
    let errorCount = 0;
    try {
      for (const alertId of ids) {
        const ok = await handleRefreshAlert(alertId, true);
        if (ok) completedCount++; else errorCount++;
      }
      setSelectedAlerts(new Set());
      showNotification(
        'Recherches terminées',
        `${completedCount} alerte${completedCount > 1 ? 's' : ''} actualisée${completedCount > 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} erreur${errorCount > 1 ? 's' : ''})` : ''}`,
        errorCount > 0 ? 'warning' : 'success'
      );
    } catch {
      showNotification('Erreur', `${completedCount} alerte${completedCount > 1 ? 's' : ''} actualisée${completedCount > 1 ? 's' : ''} avant l'erreur`, 'warning');
    } finally {
      setRefreshingAll(false);
    }
  };

  const handleRefreshAllAlerts = async () => {
    if (alerts.length === 0) {
      showNotification('Aucune alerte', 'Vous n\'avez aucune alerte à actualiser', 'info');
      return;
    }
    setRefreshingAll(true);
    let completedCount = 0;
    try {
      for (const alert of alerts) {
        await handleRefreshAlert(alert.id);
        completedCount++;
      }
      showNotification('Actualisation terminée', `${completedCount} alerte${completedCount > 1 ? 's' : ''} actualisée${completedCount > 1 ? 's' : ''}`, 'success');
    } catch (error) {
      showNotification('Erreur', `${completedCount} alerte${completedCount > 1 ? 's' : ''} actualisée${completedCount > 1 ? 's' : ''} avant l'erreur`, 'warning');
    } finally { setRefreshingAll(false); }
  };

  const handleViewResults = async (alertId: number, resultId?: number) => {
    if (isRefreshing) return;
    try {
      const token = localStorage.getItem('access_token');
      // Si un resultId précis est fourni (depuis l'historique), on charge ce scan spécifique
      const url = resultId
        ? `${API_BASE_URL}/scraper/${alertId}/results/${resultId}`
        : `${API_BASE_URL}/alerts/${alertId}/results`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const results = resultId ? (data.items || []) : (data.results || []);
        if (results.length === 0) {
          showNotification('Aucun résultat', 'Lancez une recherche pour voir les résultats de cette alerte.', 'info');
          return;
        }
        setSelectedAlertResults(results);
        setSelectedAlertAnalysis(data.analysis || calculateAnalysis(results));
        setSelectedAlertId(alertId);
        setExpandedSections({});
        setActiveFilters([]);
        setShowResultsModal(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showNotification('Oups', errorData.detail || 'Impossible de charger les résultats.', 'error');
      }
    } catch (error) { showNotification('Erreur', 'Impossible de charger les résultats', 'error'); }
  };

  const removeItem = (itemId: string | number) => {
    // Normaliser les IDs en string pour comparaison robuste
    const itemIdStr = String(itemId);
    const newResults = selectedAlertResults.filter(item => String(item.id) !== itemIdStr);
    setSelectedAlertResults(newResults);
    setSelectedAlertAnalysis(calculateAnalysis(newResults));
  };

  const loadScanHistories = async () => {
    setScanHistoriesLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/scraper/scan-histories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScanHistories(data.histories || {});
      }
    } catch { /* silencieux */ }
    finally { setScanHistoriesLoading(false); }
  };

  const handleViewSoldItems = async (alertId: number, alertName: string) => {
    setSoldItemsAlertName(alertName);
    setSoldItemsLoading(true);
    setShowSoldItemsModal(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE_URL}/scraper/${alertId}/sold-items`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSoldItems(data.sold_items || []);
      } else {
        showNotification('Erreur', 'Impossible de charger les articles vendus', 'error');
        setShowSoldItemsModal(false);
      }
    } catch {
      showNotification('Erreur', 'Erreur de connexion au serveur', 'error');
      setShowSoldItemsModal(false);
    } finally {
      setSoldItemsLoading(false);
    }
  };

  const toggleAlertSelection = (alertId: number) => {
    setSelectedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const toggleAllAlerts = () => {
    if (selectedAlerts.size === filteredAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(filteredAlerts.map(a => a.id)));
    }
  };

  const toggleFilter = (field: keyof SearchResult, value: string) => {
    // Vérifier si le filtre est déjà actif
    const filterIndex = activeFilters.findIndex(
      f => f.field === field && f.value.toLowerCase() === value.toLowerCase()
    );
    
    if (filterIndex >= 0) {
      // Désactiver le filtre (toggle off)
      setActiveFilters(prev => prev.filter((_, i) => i !== filterIndex));
    } else {
      // Activer le filtre (toggle on)
      setActiveFilters(prev => [...prev, { field, value }]);
    }
  };

  const removeFilter = (field: keyof SearchResult, value: string) => {
    setActiveFilters(prev => prev.filter(
      f => !(f.field === field && f.value.toLowerCase() === value.toLowerCase())
    ));
  };

  const removeCriteria = (field: keyof SearchResult, value: string) => {
    const newResults = selectedAlertResults.filter(item => {
      const fieldValue = item[field];
      if (!fieldValue) return true; // Garder les items sans cette propriété

      // Normaliser en minuscules pour comparaison insensible à la casse
      const fieldValueLower = typeof fieldValue === 'string' ? fieldValue.toLowerCase() : fieldValue;
      const valueLower = value.toLowerCase();

      // Gérer les couleurs multiples (ex: "noir et blanc")
      if (field === 'color' && typeof fieldValueLower === 'string' && fieldValueLower.includes(' et ')) {
        const colors = fieldValueLower.split(' et ').map(c => c.trim());
        // Retirer l'item si UNE des couleurs correspond
        return !colors.includes(valueLower);
      }

      // Sinon, comportement normal (comparaison insensible à la casse)
      return fieldValueLower !== valueLower;
    });
    setSelectedAlertResults(newResults);
    setSelectedAlertAnalysis(calculateAnalysis(newResults));
    // Retirer aussi le filtre actif si présent
    removeFilter(field, value);
  };

  const filteredAlerts = alerts.filter(alert => alert.name.toLowerCase().includes(searchQuery.toLowerCase()));

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex">
        {/* ── Panneau gauche : aurora canvas ── */}
        <div className="hidden lg:flex lg:w-1/2 flex-col relative overflow-hidden" style={{ background: '#f0fdfb' }}>
          {(() => {
            const AuroraCanvas: React.FC = () => {
              const canvasRef = React.useRef<HTMLCanvasElement>(null);
              React.useEffect(() => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                const resize = () => {
                  canvas.width  = canvas.offsetWidth;
                  canvas.height = canvas.offsetHeight;
                };
                resize();
                const ro = new ResizeObserver(resize);
                ro.observe(canvas);

                const blobs = [
                  { cx: 0.20, cy: 0.22, r: 0.42, color: [6,   182, 212], opacity: 0.50, sx: 0.28, sy: 0.21, px: 0.00, py: 0.00, amp: 0.13 },
                  { cx: 0.78, cy: 0.72, r: 0.38, color: [20,  184, 166], opacity: 0.45, sx: 0.22, sy: 0.31, px: 1.60, py: 2.40, amp: 0.11 },
                  { cx: 0.50, cy: 0.48, r: 0.30, color: [56,  189, 248], opacity: 0.35, sx: 0.19, sy: 0.26, px: 3.10, py: 1.00, amp: 0.09 },
                  { cx: 0.15, cy: 0.78, r: 0.28, color: [153, 246, 228], opacity: 0.40, sx: 0.24, sy: 0.18, px: 0.80, py: 3.50, amp: 0.10 },
                ];

                let raf: number;
                const draw = (ts: number) => {
                  const t = ts * 0.001;
                  const w = canvas.width;
                  const h = canvas.height;
                  ctx.clearRect(0, 0, w, h);

                  // fond
                  ctx.fillStyle = '#f0fdfb';
                  ctx.fillRect(0, 0, w, h);

                  blobs.forEach(b => {
                    const x = (b.cx + Math.sin(t * b.sx + b.px) * b.amp) * w;
                    const y = (b.cy + Math.cos(t * b.sy + b.py) * b.amp) * h;
                    const r = b.r * Math.min(w, h);
                    const [R, G, B] = b.color;
                    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
                    g.addColorStop(0,   `rgba(${R},${G},${B},${b.opacity})`);
                    g.addColorStop(0.5, `rgba(${R},${G},${B},${b.opacity * 0.4})`);
                    g.addColorStop(1,   `rgba(${R},${G},${B},0)`);
                    ctx.fillStyle = g;
                    ctx.beginPath();
                    ctx.ellipse(x, y, r, r * 0.85, t * 0.05, 0, Math.PI * 2);
                    ctx.fill();
                  });

                  raf = requestAnimationFrame(draw);
                };
                raf = requestAnimationFrame(draw);
                return () => { cancelAnimationFrame(raf); ro.disconnect(); };
              }, []);
              return (
                <canvas
                  ref={canvasRef}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}
                />
              );
            };
            return <AuroraCanvas />;
          })()}

          {/* Logo */}
          <div className="relative z-10 p-8 flex items-center gap-3">
            <img src="/logo192.png" alt="SmartResell" className="w-9 h-9 rounded-xl shadow-sm" />
            <span className="text-lg font-bold bg-gradient-to-r from-cyan-700 to-teal-700 bg-clip-text text-transparent">
              SmartResell
            </span>
          </div>

          <div className="relative z-10 flex-1" />
        </div>

        {/* ── Panneau droit : formulaire ── */}
        <div className="w-full lg:w-1/2 bg-white flex flex-col min-h-screen">
          <div className="p-6">
            <a href="/" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors">
              ← Accueil
            </a>
          </div>
          <div className="flex-1 flex items-center justify-center px-8">
            <div className="w-full max-w-sm">
              <div className="flex flex-col items-center text-center mb-8">
                <img src="/logo192.png" alt="SmartResell" className="w-14 h-14 rounded-2xl mb-4 shadow-md" />
                <h1 className="text-2xl font-bold text-gray-900 mb-1.5">Bienvenue sur SmartResell</h1>
                <p className="text-sm text-gray-500">Connexion ou inscription avec Google</p>
              </div>

              {authError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  {authError}
                </div>
              )}

              <button
                onClick={handleGoogleLogin}
                disabled={loading}
                className="w-full px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-semibold flex items-center justify-center gap-3 disabled:opacity-50 shadow-sm"
              >
                {loading ? <RefreshCw size={20} className="animate-spin" /> : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                      <path d="M47.532 24.552c0-1.636-.132-3.2-.396-4.704H24.48v8.928h12.932c-.564 2.988-2.24 5.52-4.764 7.224v5.988h7.716c4.512-4.152 7.168-10.272 7.168-17.436z" fill="#4285F4"/>
                      <path d="M24.48 48c6.48 0 11.916-2.148 15.888-5.82l-7.716-5.988c-2.148 1.44-4.896 2.304-8.172 2.304-6.276 0-11.592-4.236-13.5-9.936H3.024v6.18C6.984 42.816 15.168 48 24.48 48z" fill="#34A853"/>
                      <path d="M10.98 28.56A14.4 14.4 0 0 1 10.2 24c0-1.584.276-3.12.78-4.56v-6.18H3.024A23.964 23.964 0 0 0 .48 24c0 3.876.936 7.548 2.544 10.74l7.956-6.18z" fill="#FBBC05"/>
                      <path d="M24.48 9.504c3.54 0 6.708 1.212 9.204 3.6l6.888-6.888C36.396 2.388 30.96 0 24.48 0 15.168 0 6.984 5.184 3.024 13.26l7.956 6.18c1.908-5.7 7.224-9.936 13.5-9.936z" fill="#EA4335"/>
                    </svg>
                    <span>Continuer avec Google</span>
                  </>
                )}
              </button>

              <p className="mt-5 text-center text-xs text-gray-400 leading-relaxed">
                En cliquant sur "Continuer", tu acceptes nos{' '}
                <a href="/cgu" target="_blank" className="text-cyan-600 underline hover:text-teal-700 transition-colors">Conditions d'utilisation</a>
                {' '}et notre{' '}
                <a href="/confidentialite" target="_blank" className="text-cyan-600 underline hover:text-teal-700 transition-colors">Politique de confidentialité</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Ferme le sidebar et navigue (mobile)
  type Page = 'alerts' | 'subscription' | 'edit-alert' | 'optimize' | 'crm-achats' | 'crm-reception' | 'crm-stock' | 'crm-ventes' | 'crm-comptabilite' | 'crm-configuration' | 'account-settings' | 'contact';
  const navTo = (page: Page, extra?: () => void) => {
    setCurrentPage(page);
    setSidebarOpen(false);
    if (extra) extra();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Overlay mobile (fond sombre derrière le drawer) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside className={`w-64 bg-white border-r border-gray-200 flex flex-col fixed h-full z-50 transition-transform duration-300 md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity" onClick={() => setCurrentPage('alerts')}>SmartResell</h1>
            <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-lg flex-shrink-0">{user?.country || 'FR'}</span>
          </div>
        </div>
        <nav className="flex-1 p-3 overflow-y-auto flex flex-col gap-1">

          {/* ── Section Scraping des prix ── */}
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <Bell size={13} />
            <span>Scraping des prix</span>
          </div>
          <div className="pl-2 flex flex-col gap-0.5">
            {(() => {
              const isRestricted = !user?.is_unlimited && ['free', 'starter'].includes(user?.plan || '');
              return (
                <>
                  <button
                    onClick={() => { if (isRefreshing) return; navTo('alerts'); }}
                    disabled={isRefreshing}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${currentPage === 'alerts' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'} ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <Bell size={17} /><span>Alertes</span>
                    {isRestricted && <Lock size={13} className="ml-auto text-gray-400" />}
                  </button>
                  <button
                    onClick={() => { if (isRefreshing) return; navTo('optimize', loadScanHistories); }}
                    disabled={isRefreshing}
                    className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${currentPage === 'optimize' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'} ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <TrendingUp size={17} /><span>Historique</span>
                    {isRestricted && <Lock size={13} className="ml-auto text-gray-400" />}
                  </button>
                </>
              );
            })()}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 my-1" />

          {/* ── Section Vinted CRM ── */}
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <ShoppingBag size={13} />
            <span>Vinted CRM</span>
          </div>
          <div className="pl-2 flex flex-col gap-0.5">
            {(() => {
              const emailBadge = !user?.crm_email_active && (
                <span title="Email Vinted non synchronisé" className="ml-auto flex items-center justify-center w-4 h-4 rounded-full bg-orange-400 text-white text-[10px] font-bold leading-none">!</span>
              );
              const isFrance = user?.crm_email_active && user.country === 'FR';
              const crmCls = (page: string) =>
                `w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${currentPage === page ? 'bg-violet-50 text-violet-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'} ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`;
              return (
                <>
                  <button onClick={() => { if (isRefreshing) return; navTo('crm-achats'); }} disabled={isRefreshing}
                    className={crmCls('crm-achats')}>
                    <Package size={17} /><span>Achats</span>{emailBadge}
                  </button>
                  {isFrance && (
                    <button onClick={() => { if (isRefreshing) return; navTo('crm-reception'); }} disabled={isRefreshing}
                      className={crmCls('crm-reception')}>
                      <Inbox size={17} /><span>Réception</span>{emailBadge}
                    </button>
                  )}
                  <button onClick={() => { if (isRefreshing) return; navTo('crm-stock'); }} disabled={isRefreshing}
                    className={crmCls('crm-stock')}>
                    <Warehouse size={17} /><span>Stock</span>
                    {!isRefreshing && stockPendingCount > 0 ? (
                      <span className="ml-auto flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-white text-[10px] font-bold leading-none">
                        {stockPendingCount > 9 ? '9+' : stockPendingCount}
                      </span>
                    ) : !isRefreshing ? emailBadge : null}
                  </button>
                  <button onClick={() => { if (isRefreshing) return; navTo('crm-ventes'); }} disabled={isRefreshing}
                    className={crmCls('crm-ventes')}>
                    <Tag size={17} /><span>Ventes</span>
                    {!isRefreshing && salesConflictCount > 0 ? (
                      <span className="ml-auto flex items-center justify-center w-4 h-4 rounded-full bg-amber-400 text-white text-[10px] font-bold leading-none">
                        {salesConflictCount > 9 ? '9+' : salesConflictCount}
                      </span>
                    ) : !isRefreshing ? emailBadge : null}
                  </button>
                  <button onClick={() => { if (isRefreshing) return; navTo('crm-comptabilite'); }} disabled={isRefreshing}
                    className={crmCls('crm-comptabilite')}>
                    <BarChart2 size={17} /><span>Comptabilité</span>{!isRefreshing && (user?.plan === 'free' && !user?.is_unlimited ? <Lock size={13} className="ml-auto text-gray-400" /> : emailBadge)}
                  </button>
                  <button onClick={() => { if (isRefreshing) return; navTo('crm-configuration'); }} disabled={isRefreshing}
                    className={crmCls('crm-configuration')}>
                    <Settings size={17} /><span>Configuration</span>
                  </button>
                </>
              );
            })()}
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 my-1" />

          {/* ── Mon espace ── */}
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-widest">
            <UserIcon size={13} />
            <span>Mon espace</span>
          </div>
          <div className="pl-2 flex flex-col gap-0.5">
            <button
              onClick={() => !isRefreshing && navTo('subscription')}
              disabled={isRefreshing}
              className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${currentPage === 'subscription' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'} ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <Crown size={17} /><span>Abonnement</span>
            </button>
            <button
              onClick={() => !isRefreshing && navTo('contact')}
              disabled={isRefreshing}
              className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all text-sm ${currentPage === 'contact' ? 'bg-cyan-50 text-cyan-700 font-semibold' : 'text-gray-600 hover:bg-gray-50'} ${isRefreshing ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <Mail size={17} /><span>Contact</span>
            </button>
          </div>

        </nav>
        <div className="p-4 border-t border-gray-200 relative">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.full_name || 'Utilisateur'}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
            </div>
            <button
              onClick={() => setUserMenuOpen(prev => !prev)}
              className="ml-2 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
              title="Options"
            >
              <MoreHorizontal size={18} />
            </button>
          </div>
          {userMenuOpen && (
            <div className="absolute bottom-16 left-4 right-4 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <button
                onClick={() => { setUserMenuOpen(false); setCurrentPage('account-settings'); navigate('/app?page=account-settings'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-all"
              >
                <UserCog size={16} className="text-gray-400" />
                Paramètres du compte
              </button>
              <div className="h-px bg-gray-100" />
              <button
                onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut size={16} />
                Se déconnecter
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Modal Hunter quota épuisé ── */}
      {showUpgrade?.reason === 'hunter_exhausted' && (
        <div className="fixed inset-0 bg-black/60 z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Quota de recherches atteint</h3>
            <p className="text-sm text-gray-600 mb-5">Vous avez utilisé vos 25 recherches du mois. Passez au plan Business pour bénéficier de 100 recherches mensuelles.</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => { setShowUpgrade(null); setCurrentPage('subscription'); }}
                className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full font-semibold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow"
              >
                Voir les plans
              </button>
              <button
                onClick={() => setShowUpgrade(null)}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-full font-semibold text-sm hover:bg-gray-200 transition-all"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 md:ml-64 h-screen flex flex-col relative">
        {/* Top bar mobile (hamburger + logo) */}
        <div className="md:hidden flex items-center gap-3 px-4 h-14 bg-white border-b border-gray-200 flex-shrink-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Menu size={20} />
          </button>
          <span className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            SmartResell
          </span>
          <span className="ml-auto text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">{user?.country || 'FR'}</span>
        </div>
      {currentPage === 'account-settings' ? (
        <AccountSettings
          user={user}
          onUserUpdate={(updated) => setUser(prev => prev ? { ...prev, ...updated } : prev)}
          onAccountDeleted={() => { localStorage.removeItem('access_token'); setIsAuthenticated(false); setUser(null); }}
          showNotification={showNotification}
        />
      ) : currentPage === 'crm-configuration' ? (
        <CrmConfiguration showNotification={showNotification} />
      ) : currentPage === 'crm-achats' ? (
        <CrmAchats showNotification={showNotification} crm_email_active={user?.crm_email_active} is_free_plan={user?.plan === 'free' && !user?.is_unlimited} onUpgrade={() => navTo('subscription')} />
      ) : currentPage === 'crm-reception' ? (
        <CrmReception showNotification={showNotification} crm_email_active={user?.crm_email_active} is_free_plan={user?.plan === 'free' && !user?.is_unlimited} onUpgrade={() => navTo('subscription')} />
      ) : currentPage === 'crm-stock' ? (
        <CrmStock showNotification={showNotification} onPendingCountChange={fetchStockPendingCount} crm_email_active={user?.crm_email_active} is_free_plan={user?.plan === 'free' && !user?.is_unlimited} onUpgrade={() => navTo('subscription')} />
      ) : currentPage === 'crm-ventes' ? (
        <CrmVentes showNotification={showNotification} onConflictCountChange={fetchSalesConflictCount} crm_email_active={user?.crm_email_active} is_free_plan={user?.plan === 'free' && !user?.is_unlimited} onUpgrade={() => navTo('subscription')} />
      ) : currentPage === 'crm-comptabilite' ? (
        <CrmComptabilite showNotification={showNotification} crm_email_active={user?.crm_email_active} is_free_plan={user?.plan === 'free' && !user?.is_unlimited} onUpgrade={() => navTo('subscription')} />
      ) : currentPage === 'contact' ? (
        <div className="p-8 w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-1">Contact</h2>
          <p className="text-gray-500 mb-8">Une question, un bug, une suggestion ? On vous répond rapidement.</p>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <Mail className="text-white" size={20} />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900 mb-0.5">Email</p>
                <p className="text-sm text-gray-500">Réponse sous 24–48 h (jours ouvrés)</p>
              </div>
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText('contact@smart-resell.com'); setContactEmailCopied(true); setTimeout(() => setContactEmailCopied(false), 2000); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold text-sm hover:from-teal-600 hover:to-cyan-600 transition-all shadow"
            >
              {contactEmailCopied ? <Check size={15} /> : <Mail size={15} />}
              {contactEmailCopied ? 'Copié !' : 'contact@smart-resell.com'}
            </button>
          </div>

          <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
            <p className="text-sm font-semibold text-gray-700 mb-1">Support technique</p>
            <p className="text-sm text-gray-500 leading-relaxed">
              Pour un bug ou un problème avec votre compte, merci de préciser votre adresse email SmartResell et une description du problème dans votre message.
            </p>
          </div>
        </div>
      ) : (
          <div className="flex-1 overflow-auto">
            {currentPage === 'alerts' && (() => {
            const isRestricted = !user?.is_unlimited && ['free', 'starter'].includes(user?.plan || '');
            return (
            <>
            <div className="relative flex-1 min-h-0">
              {/* Blur overlay plan insuffisant */}
              {isRestricted && (
                <div className="fixed inset-0 z-30 flex items-center justify-center bg-white/40 backdrop-blur-sm">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center border border-gray-100">
                    <div className="w-14 h-14 rounded-full bg-cyan-50 flex items-center justify-center mx-auto mb-4">
                      <Lock size={28} className="text-cyan-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Section réservée</h3>
                    <p className="text-sm text-gray-600 mb-6">Le scraping des prix n'est pas inclus dans votre plan actuel. Passez au plan Hunter ou Business pour débloquer les alertes et l'historique.</p>
                    <button
                      onClick={() => setCurrentPage('subscription')}
                      className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full font-semibold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow"
                    >
                      Voir les plans
                    </button>
                  </div>
                </div>
              )}
              <div className={isRestricted ? 'pointer-events-none select-none blur-sm' : ''}>
              {isRefreshing && (
                <div className="sticky top-0 z-40 bg-amber-100 border-b border-amber-300 px-8 py-3 flex items-center justify-center gap-2">
                  <RefreshCw size={20} className="animate-spin text-amber-700" />
                  <span className="font-medium text-amber-800">Recherches en cours - Veuillez patienter, ne quittez pas la page</span>
                </div>
              )}
              {/* Header sticky avec boutons flottants */}
              <div className="sticky top-0 z-30 bg-gradient-to-b from-gray-50 via-gray-50 to-transparent pb-4 pt-8 px-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-3xl font-bold text-gray-900">Mes Alertes</h2>
                    {/* Compteur de recherches pour Hunter / Business */}
                    {user && (user.plan === 'hunter' || user.plan === 'business') && !user.is_unlimited && (
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div
                            className="bg-teal-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${Math.min(100, ((user.monthly_searches_used || 0) / (user.plan === 'hunter' ? 25 : 100)) * 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold text-gray-600">
                          {user.monthly_searches_used || 0}/{user.plan === 'hunter' ? 25 : 100} recherches
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => {
                        if (selectedAlerts.size === 0) {
                          handleRefreshAllAlerts();
                        } else {
                          handleRefreshSelectedAlerts();
                        }
                      }}
                      disabled={refreshingAll || alerts.length === 0}
                      className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center space-x-2 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <RefreshCw size={20} className={refreshingAll ? 'animate-spin' : ''} />
                      <span>Actualiser</span>
                    </button>
                    <button onClick={() => !isRefreshing && navigate('/alert/new')} disabled={isRefreshing} className={`px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl hover:from-cyan-600 hover:to-teal-600 transition-all flex items-center space-x-2 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}><Plus size={20} /><span>Nouvelle alerte</span></button>
                    <button
                      onClick={() => {
                        if (isRefreshing) return;
                        if (selectedAlerts.size === 0) {
                          showNotification('Aucune sélection', 'Veuillez sélectionner au moins une alerte à supprimer', 'info');
                          return;
                        }
                        setShowDeleteMultipleModal(true);
                      }}
                      disabled={alerts.length === 0 || isRefreshing}
                      className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl hover:from-red-600 hover:to-rose-600 transition-all flex items-center space-x-2 font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      <Trash2 size={20} />
                      <span>Supprimer</span>
                    </button>
                  </div>
                </div>
                <div className="relative"><Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="text" placeholder="Rechercher une alerte..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm" /></div>
              </div>

              {/* Contenu scrollable */}
              <div className="px-8 pb-8">
              {filteredAlerts.length > 0 && (
                <div className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    checked={selectedAlerts.size === filteredAlerts.length && filteredAlerts.length > 0}
                    onChange={toggleAllAlerts}
                    disabled={isRefreshing}
                    className={`w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500 ${isRefreshing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                  />
                  <span className="ml-3 text-sm text-gray-600 font-medium">Tout sélectionner</span>
                </div>
              )}
              <div className="space-y-4">
                {filteredAlerts.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200"><Bell className="mx-auto mb-4 text-gray-300" size={48} /><p className="text-gray-500 text-lg">Aucune alerte trouvée</p></div>
                ) : (
                  filteredAlerts.map(alert => (
                    <div key={alert.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedAlerts.has(alert.id)}
                            onChange={() => toggleAlertSelection(alert.id)}
                            disabled={isRefreshing}
                            className={`w-5 h-5 text-cyan-600 rounded focus:ring-cyan-500 flex-shrink-0 ${isRefreshing ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                          />
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">{alert.name}</h3>
                            {/* Prix de marché IQR — couleur selon tendance via historique */}
                            {alert.last_avg_price != null && (() => {
                              const fmtP = (v: number) => Number.isInteger(v) ? `${v}` : v.toFixed(2);
                              const low = alert.last_avg_price - 5;
                              // Utiliser l'historique chargé pour déterminer la tendance
                              const history = scanHistories[alert.id];
                              const scanCount = history ? history.length : null;
                              // Vrai premier scan : l'historique contient exactement 1 scan ET il est marqué is_first
                              const isFirst = scanCount === 1 || (history != null && history.length > 0 && history[0]?.is_first === true);
                              // Comparer le scan le plus récent au précédent dans l'historique
                              const prevMarket = history && history.length >= 2 ? history[1].market_price : null;
                              const currMarket = history && history.length >= 1 ? history[0].market_price : alert.last_avg_price;
                              const isUp = !isFirst && prevMarket != null && currMarket != null && currMarket > prevMarket;
                              const isDown = !isFirst && prevMarket != null && currMarket != null && currMarket < prevMarket;
                              const priceColor = !history ? 'text-emerald-600'
                                : isFirst ? 'text-violet-600'
                                : isUp ? 'text-emerald-600'
                                : isDown ? 'text-red-500'
                                : 'text-emerald-600';
                              const trendIcon = !history ? null : isFirst ? '🆕' : isUp ? '↑' : isDown ? '↓' : null;
                              const trendColor = isFirst ? 'text-violet-400' : isUp ? 'text-emerald-500' : isDown ? 'text-red-400' : 'text-gray-400';
                              const prevLow = prevMarket != null ? prevMarket - 5 : null;
                              const variation = (!isFirst && prevMarket != null && currMarket != null && prevMarket !== 0)
                                ? ((currMarket - prevMarket) / prevMarket) * 100
                                : null;
                              return (
                                <>
                                  <div className="flex items-baseline gap-1.5 mb-0.5">
                                    <span className={`text-2xl font-bold ${priceColor}`}>
                                      {fmtP(low)}€ – {fmtP(alert.last_avg_price)}€
                                    </span>
                                    {trendIcon && (
                                      <span className={`text-xs font-bold ${trendColor}`}>{trendIcon}</span>
                                    )}
                                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">estimation marché</span>
                                  </div>
                                  {prevLow != null && prevMarket != null && variation != null && (
                                    <div className="flex items-center gap-1 mb-2 text-xs text-gray-400">
                                      <span>vs</span>
                                      <span>{fmtP(prevLow)}€ – {fmtP(prevMarket)}€</span>
                                      <span className="text-gray-300">·</span>
                                      <span className={`font-semibold ${variation > 0.05 ? 'text-emerald-500' : variation < -0.05 ? 'text-red-400' : 'text-gray-400'}`}>
                                        {variation >= 0 ? '+' : ''}{variation.toFixed(1)}%
                                      </span>
                                    </div>
                                  )}
                                  {(prevLow == null || prevMarket == null || variation == null) && <div className="mb-2" />}
                                </>
                              );
                            })()}
                          {alert.category_path && <div className="mb-3"><span className="inline-flex items-center px-3 py-1 bg-purple-50 border border-purple-200 text-purple-700 rounded-lg text-sm font-medium">📂 {alert.category_path}</span></div>}
                          <div className="flex flex-wrap items-center gap-2 text-sm mb-3">
                            {alert.trending_only && <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-lg font-medium">🔥 En demande</span>}
                            {alert.max_pages != null && (
                              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg font-medium">📄 {alert.max_pages} pages</span>
                            )}
                            {/* Critères de prix : utiliser min_price/max_price */}
                            {(() => {
                              const minPrice = alert.min_price;
                              const maxPrice = alert.max_price;
                              if (minPrice && maxPrice) {
                                return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">💰 {minPrice}€ - {maxPrice}€</span>;
                              } else if (minPrice) {
                                return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">💰 Min: {minPrice}€</span>;
                              } else if (maxPrice) {
                                return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">💰 Max: {maxPrice}€</span>;
                              }
                              return null;
                            })()}
                            {/* Likes : combiner min et max si les deux existent */}
                            {alert.min_likes && alert.max_likes ? (
                              <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-medium">❤️ {alert.min_likes} - {alert.max_likes} likes</span>
                            ) : (
                              <>
                                {alert.min_likes && <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-medium">❤️ Min {alert.min_likes} likes</span>}
                                {alert.max_likes && <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-lg font-medium">❤️ Max {alert.max_likes} likes</span>}
                              </>
                            )}
                            {alert.max_publication_age && <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">📅 Max {alert.max_publication_age}j</span>}

                            {/* États */}
                            {alert.selected_conditions && alert.selected_conditions.length > 0 && (
                              alert.selected_conditions.length === 1 ? (
                                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg font-medium">💎 {alert.selected_conditions[0]}</span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`conditions-${alert.id}`]: !prev[`conditions-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors flex items-center gap-1"
                                  >
                                    💎 {alert.selected_conditions.length} états
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`conditions-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`conditions-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                      {alert.selected_conditions.map((condition, idx) => (
                                        <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">💎 {condition}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* Tailles */}
                            {alert.selected_sizes && alert.selected_sizes.length > 0 && (
                              alert.selected_sizes.length === 1 ? (
                                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium">📏 {typeof alert.selected_sizes[0] === 'object' ? alert.selected_sizes[0].title : alert.selected_sizes[0]}</span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`sizes-${alert.id}`]: !prev[`sizes-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium hover:bg-indigo-200 transition-colors flex items-center gap-1"
                                  >
                                    📏 {alert.selected_sizes.length} tailles
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`sizes-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`sizes-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                      <div className="grid grid-cols-3 gap-1">
                                        {alert.selected_sizes.map((size, idx) => (
                                          <div key={idx} className="px-2 py-1.5 text-sm text-center text-gray-700 hover:bg-gray-50 rounded">📏 {typeof size === 'object' ? size.title : size}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* Couleurs */}
                            {alert.selected_colors && alert.selected_colors.length > 0 && (
                              alert.selected_colors.length === 1 ? (
                                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-medium">🎨 {alert.selected_colors[0]}</span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`colors-${alert.id}`]: !prev[`colors-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors flex items-center gap-1"
                                  >
                                    🎨 {alert.selected_colors.length} couleurs
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`colors-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`colors-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] max-h-[300px] overflow-y-auto animate-in slide-in-from-top-2 duration-200">
                                      <div className="grid grid-cols-2 gap-1">
                                        {alert.selected_colors.map((color, idx) => (
                                          <div key={idx} className="px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">🎨 {color}</div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* Marques */}
                            {alert.selected_brands && alert.selected_brands.length > 0 && (
                              alert.selected_brands.length === 1 ? (
                                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg font-medium">
                                  🏷️ {typeof alert.selected_brands[0] === 'object' ? alert.selected_brands[0].title : alert.selected_brands[0]}
                                </span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`brands-${alert.id}`]: !prev[`brands-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg font-medium hover:bg-yellow-200 transition-colors flex items-center gap-1"
                                  >
                                    🏷️ {alert.selected_brands.length} marques
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`brands-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`brands-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                      {alert.selected_brands.map((brand, idx) => (
                                        <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">
                                          🏷️ {typeof brand === 'object' ? brand.title : brand}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* États des articles */}
                            {alert.selected_statuses && alert.selected_statuses.length > 0 && (
                              (() => {
                                const statusLabels: Record<number, string> = {
                                  6: 'Neuf avec étiquette',
                                  1: 'Neuf sans étiquette',
                                  2: 'Très bon état',
                                  3: 'Bon état',
                                  4: 'Satisfaisant'
                                };
                                return alert.selected_statuses.length === 1 ? (
                                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">
                                    💎 {statusLabels[alert.selected_statuses[0]]}
                                  </span>
                                ) : (
                                  <div className="relative">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedAlertFilters(prev => ({ ...prev, [`statuses-${alert.id}`]: !prev[`statuses-${alert.id}`] }));
                                      }}
                                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 transition-colors flex items-center gap-1"
                                    >
                                      💎 {alert.selected_statuses.length} états
                                      <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`statuses-${alert.id}`] ? 'rotate-90' : ''}`} />
                                    </button>
                                    {expandedAlertFilters[`statuses-${alert.id}`] && (
                                      <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                        {alert.selected_statuses.map((statusId, idx) => (
                                          <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">
                                            💎 {statusLabels[statusId]}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}

                            {/* Plateformes jeux vidéo */}
                            {alert.selected_platforms && alert.selected_platforms.length > 0 && (
                              (() => {
                                const platformLabels: Record<number, string> = {
                                  1281: 'PlayStation 5', 1280: 'PlayStation 4', 1279: 'PlayStation 3',
                                  1277: 'PlayStation 1', 1278: 'PlayStation 2', 1282: 'PlayStation Portable',
                                  1283: 'PlayStation Vita', 1291: 'Xbox Series S et X', 1290: 'Xbox One',
                                  1289: 'Xbox 360', 1288: 'Xbox (originale)', 1273: 'Nintendo Switch',
                                  6478: 'Nintendo Switch 2', 1274: 'Nintendo Wii', 1275: 'Nintendo Wii U',
                                  1272: 'Nintendo GameCube', 1265: 'Nintendo 3DS', 1264: 'Nintendo 2DS',
                                  1267: 'Nintendo DS', 1270: 'Nintendo Game Boy Advance', 1269: 'Nintendo Game Boy',
                                  1266: 'Nintendo 64', 1268: 'Nintendo Entertainment System', 1287: 'Super Nintendo',
                                  1276: 'PC et Mac', 1286: 'Steam Deck', 1284: 'Sega Dreamcast', 1285: 'Sega Mega Drive',
                                  1260: 'Atari', 1262: 'Commodore', 1259: 'ASUS ROG Ally', 1261: 'Ayaneo', 1263: 'Lenovo Legion Go'
                                };
                                const platform0 = alert.selected_platforms[0];
                                const platformId0 = typeof platform0 === 'object' ? platform0.id : platform0;
                                const platformTitle0 = typeof platform0 === 'object' ? platform0.title : platformLabels[platformId0];

                                return alert.selected_platforms.length === 1 ? (
                                  <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-lg font-medium">
                                    🎮 {platformTitle0 || `ID ${platformId0}`}
                                  </span>
                                ) : (
                                  <div className="relative">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedAlertFilters(prev => ({ ...prev, [`platforms-${alert.id}`]: !prev[`platforms-${alert.id}`] }));
                                      }}
                                      className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-lg font-medium hover:bg-cyan-200 transition-colors flex items-center gap-1"
                                    >
                                      🎮 {alert.selected_platforms.length} plateformes
                                      <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`platforms-${alert.id}`] ? 'rotate-90' : ''}`} />
                                    </button>
                                    {expandedAlertFilters[`platforms-${alert.id}`] && (
                                      <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                        {alert.selected_platforms.map((platform, idx) => {
                                          const platformId = typeof platform === 'object' ? platform.id : platform;
                                          const platformTitle = typeof platform === 'object' ? platform.title : platformLabels[platformId];
                                          return (
                                            <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">
                                              🎮 {platformTitle || `ID ${platformId}`}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}

                            {/* Classifications PEGI/ESRB */}
                            {alert.selected_ratings && alert.selected_ratings.length > 0 && (
                              (() => {
                                const ratingLabels: Record<number, string> = {
                                  160: 'PEGI 3', 161: 'PEGI 7', 162: 'PEGI 12', 164: 'PEGI 16', 163: 'PEGI 18',
                                  170: 'E – Tous publics', 171: 'E10+ – 10 ans et plus', 172: 'T – 13 ans et plus',
                                  173: 'M – 17 ans et plus', 175: 'AO – Réservé aux adultes', 174: 'RP – En attente',
                                  176: 'Non précisé', 165: 'USK 0', 166: 'USK 6', 167: 'USK 12', 168: 'USK 16', 169: 'USK 18'
                                };
                                const rating0 = alert.selected_ratings[0];
                                const ratingId0 = typeof rating0 === 'object' ? rating0.id : rating0;
                                const ratingTitle0 = typeof rating0 === 'object' ? rating0.title : ratingLabels[ratingId0];

                                return alert.selected_ratings.length === 1 ? (
                                  <span className="px-3 py-1 bg-red-100 text-red-700 rounded-lg font-medium">
                                    🎮 {ratingTitle0 || `ID ${ratingId0}`}
                                  </span>
                                ) : (
                                  <div className="relative">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedAlertFilters(prev => ({ ...prev, [`ratings-${alert.id}`]: !prev[`ratings-${alert.id}`] }));
                                      }}
                                      className="px-3 py-1 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center gap-1"
                                    >
                                      🎮 {alert.selected_ratings.length} classifications
                                      <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`ratings-${alert.id}`] ? 'rotate-90' : ''}`} />
                                    </button>
                                    {expandedAlertFilters[`ratings-${alert.id}`] && (
                                      <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                        {alert.selected_ratings.map((rating, idx) => {
                                          const ratingId = typeof rating === 'object' ? rating.id : rating;
                                          const ratingTitle = typeof rating === 'object' ? rating.title : ratingLabels[ratingId];
                                          return (
                                            <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">
                                              🎮 {ratingTitle || `ID ${ratingId}`}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()
                            )}

                            {/* Matières */}
                            {alert.selected_materials && alert.selected_materials.length > 0 && (
                              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-lg font-medium">
                                🧵 {alert.selected_materials.length} matière{alert.selected_materials.length > 1 ? 's' : ''}
                              </span>
                            )}

                            {/* Motifs */}
                            {alert.selected_patterns && alert.selected_patterns.length > 0 && (
                              <span className="px-3 py-1 bg-fuchsia-100 text-fuchsia-700 rounded-lg font-medium">
                                🎨 {alert.selected_patterns.length} motif{alert.selected_patterns.length > 1 ? 's' : ''}
                              </span>
                            )}

                            {/* Langues */}
                            {alert.selected_languages && alert.selected_languages.length > 0 && (
                              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg font-medium">
                                🌍 {alert.selected_languages.length} langue{alert.selected_languages.length > 1 ? 's' : ''}
                              </span>
                            )}

                            {/* Mots à inclure */}
                            {alert.must_include && alert.must_include.length > 0 && (
                              alert.must_include.length === 1 ? (
                                <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-lg font-medium">✅ {alert.must_include[0]}</span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`include-${alert.id}`]: !prev[`include-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-teal-100 text-teal-700 rounded-lg font-medium hover:bg-teal-200 transition-colors flex items-center gap-1"
                                  >
                                    ✅ {alert.must_include.length} inclus
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`include-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`include-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                      {alert.must_include.map((word, idx) => (
                                        <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">✅ {word}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            )}

                            {/* Mots à exclure */}
                            {alert.must_exclude && alert.must_exclude.length > 0 && (
                              alert.must_exclude.length === 1 ? (
                                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-lg font-medium">🚫 {alert.must_exclude[0]}</span>
                              ) : (
                                <div className="relative">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedAlertFilters(prev => ({ ...prev, [`exclude-${alert.id}`]: !prev[`exclude-${alert.id}`] }));
                                    }}
                                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center gap-1"
                                  >
                                    🚫 {alert.must_exclude.length} exclus
                                    <ChevronRight size={14} className={`transform transition-transform ${expandedAlertFilters[`exclude-${alert.id}`] ? 'rotate-90' : ''}`} />
                                  </button>
                                  {expandedAlertFilters[`exclude-${alert.id}`] && (
                                    <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-2 z-10 min-w-[200px] animate-in slide-in-from-top-2 duration-200">
                                      {alert.must_exclude.map((word, idx) => (
                                        <div key={idx} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded">🚫 {word}</div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                          {refreshingAlerts[alert.id] && (
                            <div className="mt-4"><div className="flex justify-between text-xs text-gray-600 mb-2"><span>Recherche en cours...</span><span className="font-semibold">{refreshingAlerts[alert.id].progress}%</span></div><div className="w-full bg-gray-200 rounded-full h-2.5"><div className="bg-gradient-to-r from-cyan-500 to-teal-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${refreshingAlerts[alert.id].progress}%` }} /></div></div>
                          )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <button onClick={() => handleEditAlert(alert)} disabled={isRefreshing} className={`p-3 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-all ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}><Edit size={18} /></button>
                          <button onClick={() => handleViewResults(alert.id)} disabled={isRefreshing} className={`p-3 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`} title="Voir les résultats"><Eye size={18} /></button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              </div>
              </div>
            </div>
            </>
            );
          })()}

          {currentPage === 'subscription' && (() => {
            const daysLeft = getLaunchDaysLeft();
            const isFr = typeof navigator !== 'undefined' && navigator.language.startsWith('fr');
            const currentPlan = user?.plan || 'free';
            const isUnlimited = user?.is_unlimited || false;
            const searchesUsed = user?.monthly_searches_used || 0;

            type SubFeature = { label: string; strikethrough?: boolean; frOnly?: boolean };
            const subPlans: {
              id: string; name: string; monthlyPrice: number; strikeMonthlyPrice: number | null;
              highlight: boolean; popularBadge?: boolean;
              desc: string; cta: string;
              features: SubFeature[];
              hasAnnual: boolean; annualPrice: number | null; annualTotal: number | null;
            }[] = [
              {
                id: 'free', name: 'Gratuit', monthlyPrice: 0, strikeMonthlyPrice: null,
                highlight: false,
                desc: 'Pour découvrir sans engagement.',
                cta: 'Plan actuel',
                features: [
                  { label: '10 ventes visibles' },
                  { label: '10 achats visibles' },
                  { label: '10 articles en stock traités' },
                  { label: 'CRM illimité', strikethrough: true },
                  { label: 'Scraping des prix', strikethrough: true },
                  { label: 'Recherche de niches', strikethrough: true },
                ],
                hasAnnual: false, annualPrice: null, annualTotal: null,
              },
              {
                id: 'starter', name: 'Starter', monthlyPrice: 10, strikeMonthlyPrice: 18,
                highlight: false,
                desc: 'CRM Vinted complet pour gérer vos transactions.',
                cta: 'Choisir Starter',
                features: [
                  { label: 'CRM complet illimité' },
                  { label: 'Recadrage des bordereaux d\'expédition' },
                  { label: 'Traitement QR code & code de retrait colis', frOnly: true },
                  { label: 'Comptabilité intégrée' },
                  { label: 'Scraping des prix des articles', strikethrough: true },
                  { label: 'Recherche de niches', strikethrough: true },
                ],
                hasAnnual: true, annualPrice: 7.5, annualTotal: 90,
              },
              {
                id: 'hunter', name: 'Hunter', monthlyPrice: 18, strikeMonthlyPrice: 25,
                highlight: true, popularBadge: true,
                desc: 'CRM + Scraping des prix pour maximiser vos reventes.',
                cta: 'Choisir Hunter',
                features: [
                  { label: 'Tout du plan Starter' },
                  { label: '25 recherches de prix incluses (niches & veille)' },
                  { label: 'Historique & analyse avancée par alerte' },
                ],
                hasAnnual: true, annualPrice: 13.5, annualTotal: 162,
              },
              {
                id: 'business', name: 'Business', monthlyPrice: 35, strikeMonthlyPrice: null,
                highlight: false,
                desc: 'Pour les revendeurs professionnels à haut volume.',
                cta: 'Choisir Business',
                features: [
                  { label: 'Tout du plan Hunter' },
                  { label: '100 recherches de prix incluses' },
                ],
                hasAnnual: false, annualPrice: null, annualTotal: null,
              },
            ];

            const handleSubscribe = async (planId: string) => {
              if (planId === 'free') return;
              try {
                // Si l'utilisateur a déjà un plan payant → portail Stripe (upgrade/downgrade)
                if (currentPlan !== 'free') {
                  const res = await apiClient.post('/payments/portal-session', {});
                  if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
                  return;
                }
                // Sinon → nouveau paiement via Stripe Checkout
                const billing = subBillingAnnual ? 'annual' : 'monthly';
                const { data } = await apiClient.post(`/payments/subscribe/${planId}`, { billing });
                if (data?.checkout_url) {
                  window.location.href = data.checkout_url;
                }
              } catch (err: any) {
                setCustomModal({
                  show: true, title: 'Erreur paiement',
                  message: err?.detail || err?.message || 'Impossible de démarrer le paiement. Réessayez.',
                  type: 'error',
                });
              }
            };

            const PLAN_ORDER = ['free', 'starter', 'hunter', 'business'];
            const userPlanIdx = PLAN_ORDER.indexOf(currentPlan);
            const userInterval = user?.subscription_interval || null; // 'monthly' | 'annual' | null

            const renderSubCard = (plan: typeof subPlans[0]) => {
              const showAnnual = subBillingAnnual && plan.hasAnnual;
              const displayPrice = showAnnual && plan.annualPrice != null ? plan.annualPrice : plan.monthlyPrice;
              const strikePrice = showAnnual ? null : plan.strikeMonthlyPrice;
              const visibleFeatures = plan.features.filter(f => !f.frOnly || isFr);
              const cardPlanIdx = PLAN_ORDER.indexOf(plan.id);
              const cardIsAnnual = showAnnual && plan.hasAnnual;

              // Plan actuel élargi : couvre les plans de rang inférieur ou égal selon la logique mensuel/annuel
              const isCurrentPlan = (() => {
                if (isUnlimited) return false;
                if (plan.id === 'free') return currentPlan === 'free';
                if (cardPlanIdx > userPlanIdx) return false; // plan supérieur : jamais "actuel"
                if (!userInterval || userInterval === 'monthly') {
                  // Abonnement mensuel : seuls les plans mensuels ≤ rang actuel sont "Plan actuel"
                  return !cardIsAnnual;
                } else {
                  // Abonnement annuel : annuels ≤ rang actuel + mensuels correspondants = "Plan actuel"
                  return true;
                }
              })();

              const isDisabled = plan.id === 'free' || isCurrentPlan;

              return (
                <div className={`rounded-2xl p-6 flex flex-col h-full transition-all ${
                  plan.highlight
                    ? 'bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-xl shadow-teal-200'
                    : 'bg-white border border-gray-200 shadow-sm'
                }`}>
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className={`text-base font-bold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                      {plan.popularBadge && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-yellow-400 text-yellow-900">⭐ Populaire</span>
                      )}
                    </div>
                    <p className={`text-xs mb-3 leading-relaxed ${plan.highlight ? 'text-teal-100' : 'text-gray-500'}`}>{plan.desc}</p>
                    {strikePrice != null && (
                      <div className="text-sm mb-0.5">
                        <span className={`line-through ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>{strikePrice % 1 === 0 ? strikePrice : strikePrice.toFixed(2).replace('.', ',')}€</span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className={`text-4xl font-extrabold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                        {displayPrice === 0 ? '0€' : `${displayPrice % 1 === 0 ? displayPrice : displayPrice.toFixed(2).replace('.', ',')}€`}
                      </span>
                      {displayPrice > 0 && <span className={`text-xs ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>/mois</span>}
                    </div>
                    {showAnnual && plan.annualTotal != null && (
                      <p className={`text-xs mt-1 ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>
                        {plan.annualTotal % 1 === 0 ? plan.annualTotal : plan.annualTotal.toFixed(2).replace('.', ',')}€/an
                      </p>
                    )}
                    {!plan.hasAnnual && plan.id !== 'free' && subBillingAnnual && (
                      <p className={`text-xs mt-1 ${plan.highlight ? 'text-teal-200' : 'text-gray-400'}`}>Mensuel uniquement</p>
                    )}
                  </div>
                  <ul className="space-y-2 mb-5 flex-1">
                    {visibleFeatures.map((feat) => (
                      <li key={feat.label} className="flex items-start gap-2.5">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${feat.strikethrough ? plan.highlight ? 'bg-white/10' : 'bg-gray-100' : plan.highlight ? 'bg-white/20' : 'bg-teal-50'}`}>
                          <Check className={`w-2.5 h-2.5 ${feat.strikethrough ? plan.highlight ? 'text-white/30' : 'text-gray-300' : plan.highlight ? 'text-white' : 'text-teal-600'}`} />
                        </div>
                        <span className={`text-xs leading-relaxed ${feat.strikethrough ? plan.highlight ? 'line-through text-white/40' : 'line-through text-gray-400' : plan.highlight ? 'text-teal-50' : 'text-gray-600'}`}>{feat.label}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleSubscribe(plan.id)}
                    className={`w-full py-3 rounded-xl text-sm font-semibold transition-all ${isDisabled ? 'bg-gray-100 text-gray-400 cursor-default' : plan.highlight ? 'bg-white text-teal-700 hover:bg-teal-50 shadow' : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-md'}`}
                  >
                    {isCurrentPlan ? '✓ Plan actuel' : plan.cta}
                  </button>
                </div>
              );
            };

            const planLabels: Record<string, string> = {
              free: 'Gratuit', starter: 'Starter', hunter: 'Hunter', business: 'Business',
            };
            const nextPlan: Record<string, string> = {
              free: 'starter', starter: 'hunter', hunter: 'business',
            };

            const formatDate = (ts: number) =>
              new Date(ts * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

            return (
              <div className="p-8 w-full">

                {/* ── En-tête ── */}
                <h2 className="text-3xl font-bold text-gray-900 mb-1">Abonnement</h2>
                <p className="text-gray-500 mb-8">
                  Retrouve ton offre actuelle et change de formule quand tu veux
                </p>

                {/* ── Section Plan actuel (même gabarit compact qu’avant : une ligne, bouton à droite) ── */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 mb-8">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                        <Crown className="text-white" size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">Plan actuel</h3>
                          {isUnlimited
                            ? <span className="text-xs bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2.5 py-0.5 rounded-full font-bold">Accès illimité</span>
                            : currentPlan === 'free'
                              ? <span className="text-xs bg-gray-100 text-gray-500 border border-gray-200 px-2.5 py-0.5 rounded-full font-medium">Limité</span>
                              : <span className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-0.5 rounded-full font-medium">Actif</span>
                          }
                        </div>
                        <p className="text-sm text-gray-600">
                          Tu es actuellement sur le plan <strong className="text-gray-900">{isUnlimited ? 'Business (Illimité)' : planLabels[currentPlan] || 'Gratuit'}</strong>
                          {currentPlan === 'free' && !isUnlimited && ' — 10 éléments max par section CRM, scraping des prix non disponible.'}
                          {currentPlan === 'starter' && ' — CRM illimité, scraping des prix non inclus.'}
                          {(currentPlan === 'hunter' || currentPlan === 'business') && !isUnlimited && (
                            <span> — <strong>{searchesUsed}</strong> recherche{searchesUsed > 1 ? 's' : ''} utilisée{searchesUsed > 1 ? 's' : ''} ce mois sur {currentPlan === 'hunter' ? '25' : '100'}.</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 w-full sm:w-auto">
                      {user?.plan && user.plan !== 'free' && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await apiClient.post('/payments/portal-session', {});
                              window.location.href = res.data.checkout_url;
                            } catch (e: any) {
                              setCustomModal({ show: true, title: 'Erreur', message: e?.detail || 'Impossible d\'ouvrir le portail.', type: 'error' });
                            }
                          }}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
                        >
                          Gérer mon abonnement
                        </button>
                      )}
                      {(nextPlan[currentPlan] || isUnlimited || currentPlan === 'business') && (
                        <button
                          type="button"
                          onClick={() => plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-md hover:from-teal-600 hover:to-cyan-600 transition-all"
                        >
                          Passez au niveau suivant
                          <ChevronDown size={18} className="flex-shrink-0" aria-hidden />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Titre + toggle ── */}
                <div ref={plansRef}>
                  <h3 className="text-xl font-bold text-gray-900 mb-6">Choisissez votre plan</h3>
                </div>

                <div className="w-full flex justify-center mb-8">
                  <div className="inline-flex items-center gap-3 bg-white border border-gray-200 rounded-full px-2 py-1.5 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setSubBillingAnnual(false)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium font-body transition-all ${!subBillingAnnual ? 'bg-teal-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Mensuel
                    </button>
                    <button
                      type="button"
                      onClick={() => setSubBillingAnnual(true)}
                      className={`px-4 py-1.5 rounded-full text-sm font-medium font-body transition-all flex items-center gap-1.5 ${subBillingAnnual ? 'bg-teal-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      Annuel
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${subBillingAnnual ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-700'}`}>-25%</span>
                    </button>
                  </div>
                </div>

                {/* Mobile */}
                <div className="xl:hidden flex flex-col gap-5">
                  {renderSubCard(subPlans[0])}
                  <div className="border-2 border-orange-400 rounded-2xl p-4 flex flex-col gap-4 bg-orange-50/20">
                    <div className="text-center">
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-4 py-2 text-xs font-semibold shadow-md">
                        <Clock size={13} className="flex-shrink-0" />
                        {daysLeft > 0 ? `Offre lancement — encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''}` : 'Offre terminée'}
                      </div>
                    </div>
                    {renderSubCard(subPlans[1])}
                    {renderSubCard(subPlans[2])}
                  </div>
                  {renderSubCard(subPlans[3])}
                </div>

                {/* Desktop : 4 colonnes égales + encadrement orange élargi */}
                <div className="hidden xl:block relative" style={{ paddingTop: '4.5rem', paddingBottom: '1.5rem' }}>
                  <div
                    className="absolute border-2 border-orange-400 rounded-3xl pointer-events-none"
                    style={{ left: 'calc(25% - 6px)', right: 'calc(25% - 6px)', top: '0', bottom: '0', zIndex: 20, background: 'rgba(255, 237, 213, 0.12)' }}
                  />
                  <div className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap" style={{ top: '1rem', zIndex: 30 }}>
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full px-5 py-2 text-sm font-semibold shadow-md">
                      <Clock size={14} className="flex-shrink-0" />
                      {daysLeft > 0
                        ? `Offre de lancement — encore ${daysLeft} jour${daysLeft > 1 ? 's' : ''} (31 mai 2026)`
                        : 'Offre de lancement terminée'}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-6 items-stretch relative z-10">
                    {subPlans.map(plan => (
                      <div key={plan.id} className="flex flex-col">{renderSubCard(plan)}</div>
                    ))}
                  </div>
                </div>

                {/* ── Informations de facturation ── */}
                <div className="mt-12">
                  <h3 className="text-xl font-bold text-gray-900 mb-1">Informations de facturation</h3>
                  <p className="text-sm text-gray-500 mb-4">Vos factures et reçus SmartResell</p>
                  <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                    {invoicesLoading ? (
                      <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
                        <RefreshCw size={20} className="animate-spin" />
                        <span className="text-sm">Chargement des factures…</span>
                      </div>
                    ) : invoices.length === 0 ? (
                      <div className="py-12 text-center">
                        <CreditCard className="mx-auto mb-3 text-gray-300" size={36} />
                        <p className="text-sm font-medium text-gray-500">Aucune facture disponible</p>
                        <p className="text-xs text-gray-400 mt-1">Vos factures apparaîtront ici après votre premier paiement.</p>
                      </div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-100 bg-gray-50">
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Facture</th>
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Période</th>
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant</th>
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                            <th className="px-5 py-3" />
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv: any, i: number) => (
                            <tr key={inv.id} className={`border-b border-gray-50 hover:bg-gray-50 transition-colors ${i === invoices.length - 1 ? 'border-b-0' : ''}`}>
                              <td className="px-5 py-3.5 text-gray-700 font-medium">{inv.number || inv.description || 'Abonnement'}</td>
                              <td className="px-5 py-3.5 text-gray-500">
                                {inv.period_start && inv.period_end
                                  ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}`
                                  : formatDate(inv.created)}
                              </td>
                              <td className="px-5 py-3.5 font-semibold text-gray-900">
                                {inv.amount_paid.toFixed(2).replace('.', ',')} {inv.currency}
                              </td>
                              <td className="px-5 py-3.5">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  inv.status === 'paid' ? 'bg-green-50 text-green-700 border border-green-200' :
                                  inv.status === 'open' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                                  'bg-gray-100 text-gray-600 border border-gray-200'
                                }`}>
                                  {inv.status === 'paid' ? 'Payé' : inv.status === 'open' ? 'En attente' : inv.status}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-right">
                                {inv.pdf_url && (
                                  <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 px-3 py-1.5 rounded-lg transition-colors">
                                    <ExternalLink size={12} /> PDF
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

              </div>
            );
          })()}

          {currentPage === 'optimize' && (() => {
            const isRestricted = !user?.is_unlimited && ['free', 'starter'].includes(user?.plan || '');
            return (
            <>
            <div className="relative flex-1 min-h-0">
              {isRestricted && (
                <div className="fixed inset-0 z-30 flex items-center justify-center bg-white/40 backdrop-blur-sm">
                  <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center border border-gray-100">
                    <div className="w-14 h-14 rounded-full bg-cyan-50 flex items-center justify-center mx-auto mb-4">
                      <Lock size={28} className="text-cyan-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Section réservée</h3>
                    <p className="text-sm text-gray-600 mb-6">L'historique des scans n'est pas inclus dans votre plan actuel. Passez au plan Hunter ou Business pour y accéder.</p>
                    <button
                      onClick={() => setCurrentPage('subscription')}
                      className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full font-semibold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow"
                    >
                      Voir les plans
                    </button>
                  </div>
                </div>
              )}
              <div className={isRestricted ? 'pointer-events-none select-none blur-sm' : ''}>
            <div className="p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Historique des scans</h2>
                <p className="text-gray-500">Suivez l'évolution des prix du marché sur vos derniers extracts.</p>
              </div>

              {alerts.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                  <TrendingUp className="mx-auto mb-4 text-gray-300" size={48} />
                  <p className="text-gray-500 text-lg font-medium">Aucune alerte disponible</p>
                  <p className="text-gray-400 text-sm mt-1">Créez des alertes et lancez des scans pour voir l'historique.</p>
                </div>
              ) : scanHistoriesLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="animate-spin text-cyan-500 mr-3" size={28} />
                  <span className="text-gray-500 text-lg">Chargement de l'historique...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {alerts.map(alert => {
                    const scans: any[] = scanHistories[alert.id] || [];
                    const fmt = (v: number) => Number.isInteger(v) ? `${v}` : v.toFixed(2);
                    return (
                      <div key={alert.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                        {/* Header alerte */}
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-gray-900 text-lg">{alert.name}</h3>
                            {alert.category_path && <p className="text-sm text-purple-600 mt-0.5">📂 {alert.category_path}</p>}
                          </div>
                          {scans.length === 0 && (
                            <span className="text-xs text-gray-400 italic">Aucun scan effectué</span>
                          )}
                        </div>
                        {/* Cards scans */}
                        {scans.length > 0 && (
                          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[...scans].map((scan, idx) => {
                              // idx 0 = le plus récent, idx n = le plus ancien
                              const prevScan = scans[idx + 1];
                              // is_first = vrai premier scan de l'alerte (marqué par le backend)
                              const isFirst = scan.is_first === true;
                              const isUp = !isFirst && prevScan && scan.market_price != null && prevScan.market_price != null && scan.market_price > prevScan.market_price;
                              const isDown = !isFirst && prevScan && scan.market_price != null && prevScan.market_price != null && scan.market_price < prevScan.market_price;
                              const diff = prevScan && scan.market_price != null && prevScan.market_price != null
                                ? Math.abs(scan.market_price - prevScan.market_price).toFixed(2)
                                : null;

                              const cardBorder = isFirst
                                ? 'border-violet-200 bg-violet-50/40'
                                : isUp ? 'border-emerald-200 bg-emerald-50/40'
                                : isDown ? 'border-red-200 bg-red-50/40'
                                : 'border-gray-200 bg-gray-50/30';
                              const priceColor = isFirst
                                ? 'text-violet-700'
                                : isUp ? 'text-emerald-700'
                                : isDown ? 'text-red-600'
                                : 'text-gray-700';

                              const date = new Date(scan.scanned_at);
                              const dateStr = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
                              const timeStr = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

                              return (
                                <button
                                  key={scan.id}
                                  onClick={() => {
                                    if (idx !== 0) return;
                                    loadScanHistories();
                                    handleViewResults(alert.id, scan.id);
                                  }}
                                  className={`rounded-xl border-2 p-4 text-left transition-all ${idx === 0 ? 'hover:shadow-md hover:scale-[1.02] cursor-pointer' : 'cursor-default opacity-70'} ${cardBorder}`}
                                >
                                  {/* Badge tendance */}
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs text-gray-400">{dateStr} · {timeStr}</span>
                                    {isFirst ? (
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">1er scan</span>
                                    ) : isUp ? (
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">↑ +{diff}€</span>
                                    ) : isDown ? (
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">↓ -{diff}€</span>
                                    ) : (
                                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">stable</span>
                                    )}
                                  </div>
                                  {/* Estimation marché */}
                                  {scan.market_price != null ? (
                                    <p className={`text-base font-bold ${priceColor}`}>
                                      {fmt(scan.market_price - 5)}€ – {fmt(scan.market_price)}€
                                    </p>
                                  ) : (
                                    <p className="text-base font-bold text-gray-400">—</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">{scan.total_count} articles</p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
            </>
            );
          })()}


          {currentPage === 'edit-alert' && editingAlert && (
            <EditAlertView
              alert={editingAlert}
              onBack={handleBackToAlerts}
              onSave={handleCreateAlert}
              results={selectedAlertResults}
              analysis={selectedAlertAnalysis}
              isUnlimited={user?.is_unlimited || false}
              userPlan={user?.plan || 'free'}
            />
          )}

          </div>
      )}
      </main>

      {editingAlert && (
        <AlertModal show={showModal} editingAlert={editingAlert} onClose={() => { setShowModal(false); setEditingAlert(null); }} onSave={handleCreateAlert} isUnlimited={user?.is_unlimited || false} userPlan={user?.plan || 'free'} />
      )}

      {/* Modale de suppression simple */}
      {showDeleteModal && alertToDelete && (
        <CustomModal
          show={showDeleteModal}
          title={`Supprimer "${alertToDelete.name}" ?`}
          message="Cette action est irréversible. L'alerte sera définitivement supprimée."
          type="confirm"
          onClose={() => {
            setShowDeleteModal(false);
            setAlertToDelete(null);
          }}
          actionButton={{
            text: 'Supprimer',
            onClick: confirmDeleteAlert,
            variant: 'danger'
          }}
        />
      )}

      {/* Modale de suppression multiple */}
      {showDeleteMultipleModal && (
        <CustomModal
          show={showDeleteMultipleModal}
          title={`Supprimer ${selectedAlerts.size} alerte${selectedAlerts.size > 1 ? 's' : ''} ?`}
          message="Cette action est irréversible. Toutes les alertes sélectionnées seront définitivement supprimées."
          type="confirm"
          onClose={() => setShowDeleteMultipleModal(false)}
          actionButton={{
            text: 'Supprimer',
            onClick: confirmDeleteMultipleAlerts,
            variant: 'danger'
          }}
        />
      )}

      {/* Modales de notification */}
      <CustomModal
        show={customModal.show}
        title={customModal.title}
        message={customModal.message}
        type={customModal.type}
        onClose={() => setCustomModal({ ...customModal, show: false })}
        actionButton={customModal.actionButton}
      />

      {showResultsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full my-8 max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h2 className="text-2xl font-bold text-white">Résultats</h2>
              <button onClick={() => setShowResultsModal(false)} className="text-white hover:bg-white/20 p-2 rounded-lg"><X size={24} /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="p-6">
              {selectedAlertAnalysis && (
                <div className="mb-6 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 max-h-[400px] overflow-y-auto">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                      <TrendingUp className="text-green-600" size={24} />
                      <span>Analyse intelligente des prix</span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-gray-600 mb-1">Items analysés</div>
                      <div className="text-2xl font-bold text-gray-900">{selectedAlertAnalysis.total_items_analyzed}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-gray-600 mb-1">Prix minimum</div>
                      <div className="text-2xl font-bold text-gray-900">{selectedAlertAnalysis.overall_stats.min}€</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-gray-600 mb-1">Prix moyen</div>
                      <div className="text-2xl font-bold text-cyan-600">{selectedAlertAnalysis.overall_stats.avg.toFixed(2)}€</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-gray-600 mb-1">Prix maximum</div>
                      <div className="text-2xl font-bold text-gray-900">{selectedAlertAnalysis.overall_stats.max}€</div>
                    </div>
            </div>

                  {/* Analyse détaillée par critère */}
                  {(() => {
                    const alert = alerts.find(a => a.id === selectedAlertId);
                    const hasSelectedColors = alert?.selected_colors && alert.selected_colors.length > 0;
                    // Afficher la couleur uniquement si l'alerte a des couleurs sélectionnées
                    // (Ne pas afficher si aucune couleur n'est sélectionnée dans l'alerte, même après tri par couleurs)
                    const hasColorData = selectedAlertAnalysis.color_impact && Object.keys(selectedAlertAnalysis.color_impact).length > 0;
                    const shouldShowColor = hasSelectedColors && hasColorData;

                    return [
                      { title: "Impact de l'état", icon: "💎", field: "condition" as keyof SearchResult, data: selectedAlertAnalysis.condition_impact },
                      ...(shouldShowColor ? [{ title: "Impact de la couleur", icon: "🎨", field: "color" as keyof SearchResult, data: selectedAlertAnalysis.color_impact }] : []),
                      { title: "Impact de la taille", icon: "📏", field: "size" as keyof SearchResult, data: selectedAlertAnalysis.size_impact },
                      { title: "Impact de la marque", icon: "🏷️", field: "brand" as keyof SearchResult, data: selectedAlertAnalysis.brand_impact },
                      { title: "Impact de la catégorie", icon: "📂", field: "category_name" as keyof SearchResult, data: selectedAlertAnalysis.category_impact },
                    ];
                  })().map((section) => {
                    if (!section.data || Object.keys(section.data).length === 0) return null;
                    const entries = Object.entries(section.data as Record<string, { avg_price: number; count: number }>).sort((a, b) => b[1].count - a[1].count);
                    const isExpanded = expandedSections[section.field];
                    const visibleEntries = isExpanded ? entries : entries.slice(0, 5);
                    return (
                      <div key={section.field} className="mb-6">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                          <span>{section.icon}</span>
                          <span>{section.title}</span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {visibleEntries.map(([value, data]: [string, any]) => {
                            const isActive = activeFilters.some(
                              f => f.field === section.field && f.value.toLowerCase() === value.toLowerCase()
                            );
                            return (
                              <button
                                key={value} 
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleFilter(section.field, value);
                                }}
                                className={`group w-full flex items-center justify-between bg-white/60 p-3 rounded-lg border transition-all cursor-pointer text-left ${
                                  isActive
                                    ? 'border-cyan-500 bg-cyan-50'
                                    : 'border-transparent hover:border-green-200'
                                }`}
                              >
                                <span className="text-sm text-gray-700">{value}</span>
                                <div className="flex items-center space-x-3">
                                  <span className="text-xs text-gray-400">({data.count})</span>
                                  <span className="font-bold text-gray-900">{data.avg_price.toFixed(2)}€</span>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeCriteria(section.field, value);
                                    }} 
                                    className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              </button>
                            );
                          })}
            </div>
                        {entries.length > 5 && (
                          <button onClick={() => setExpandedSections(p => ({ ...p, [section.field]: !isExpanded }))} className="mt-2 text-xs font-semibold text-cyan-600 hover:text-cyan-700">
                            {isExpanded ? 'Voir moins' : `Voir les ${entries.length - 5} autres...`}
              </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Boutons de tri */}
              <div className="mb-6">
                <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center space-x-2">
                      <span>📊</span>
                      <span>Trier par</span>
                    </h3>
                    <div className="flex items-center space-x-3">
                      {/* Bouton Tri par Likes */}
                      <button
                        onClick={() => {
                          if (sortBy === 'likes') {
                            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                          } else {
                            setSortBy('likes');
                            setSortOrder('desc');
                          }
                        }}
                        className={`px-4 py-2 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                          sortBy === 'likes'
                            ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>❤️</span>
                        <span>Likes</span>
                        {sortBy === 'likes' && (
                          <span className="text-sm">{sortOrder === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </button>

                      {/* Bouton Tri par Prix */}
                      <button
                        onClick={() => {
                          if (sortBy === 'price') {
                            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                          } else {
                            setSortBy('price');
                            setSortOrder('asc');
                          }
                        }}
                        className={`px-4 py-2 rounded-xl font-semibold transition-all flex items-center space-x-2 ${
                          sortBy === 'price'
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>💰</span>
                        <span>Prix</span>
                        {sortBy === 'price' && (
                          <span className="text-sm">{sortOrder === 'desc' ? '↓' : '↑'}</span>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    {sortBy === 'likes'
                      ? (sortOrder === 'desc' ? 'Du plus liké au moins liké' : 'Du moins liké au plus liké')
                      : (sortOrder === 'desc' ? 'Du plus cher au moins cher' : 'Du moins cher au plus cher')
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <h3 className="text-xl font-bold text-gray-900">Articles trouvés</h3>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium">
                    {(() => {
                      // Calculer le nombre d'articles filtrés avec logique OR/AND
                      if (activeFilters.length === 0) {
                        return selectedAlertResults.length;
                      }

                      // Grouper les filtres par champ (même logique que plus bas)
                      const filtersByField = activeFilters.reduce((acc, filter) => {
                        if (!acc[filter.field]) acc[filter.field] = [];
                        acc[filter.field].push(filter.value.toLowerCase().trim());
                        return acc;
                      }, {} as Record<string, string[]>);

                      const filtered = selectedAlertResults.filter(item => {
                        return Object.entries(filtersByField).every(([field, filterValues]) => {
                          const fieldValue = item[field as keyof SearchResult];
                          if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                            return false;
                          }
                          const fieldValueLower = typeof fieldValue === 'string' ? fieldValue.toLowerCase().trim() : String(fieldValue).toLowerCase().trim();

                          return filterValues.some(filterValue => {
                            if (field === 'color' && typeof fieldValue === 'string' && fieldValueLower.includes(' et ')) {
                              const colors = fieldValueLower.split(' et ').map(c => c.trim());
                              return colors.includes(filterValue);
                            }
                            return fieldValueLower === filterValue;
                          });
                        });
                      });
                      return filtered.length;
                    })()} articles
                  </span>
                </div>
                {activeFilters.length > 0 && (
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm text-gray-600">Filtres actifs:</span>
                    {activeFilters.map((filter, index) => (
                      <span key={index} className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-lg text-sm font-medium flex items-center space-x-2">
                        <span>{filter.field === 'color' ? '🎨' : filter.field === 'condition' ? '💎' : filter.field === 'size' ? '📏' : filter.field === 'brand' ? '🏷️' : '📂'} {filter.value}</span>
                        <button
                          onClick={() => removeFilter(filter.field, filter.value)}
                          className="hover:text-red-500 transition-all"
                        >
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => setActiveFilters([])}
                      className="px-3 py-1 text-xs text-gray-600 hover:text-red-500 transition-all underline"
                    >
                      Tout effacer
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {(() => {
                  let filteredResults = [...selectedAlertResults];

                    // Appliquer tous les filtres actifs
                    // Logique: OR entre filtres de la même catégorie, AND entre catégories différentes
                    // Ex: (taille M OU taille S) ET (couleur noir OU couleur blanc) ET (marque Nike)
                    if (activeFilters.length > 0) {
                      // Grouper les filtres par champ (field)
                      const filtersByField = activeFilters.reduce((acc, filter) => {
                        if (!acc[filter.field]) acc[filter.field] = [];
                        acc[filter.field].push(filter.value.toLowerCase().trim());
                        return acc;
                      }, {} as Record<string, string[]>);

                      filteredResults = filteredResults.filter(item => {
                        // Pour chaque champ de filtre (condition, size, color, brand, etc.)
                        // L'item doit correspondre à AU MOINS UNE valeur de ce champ (OR)
                        // ET doit satisfaire TOUS les champs (AND entre champs)
                        return Object.entries(filtersByField).every(([field, filterValues]) => {
                          const fieldValue = item[field as keyof SearchResult];

                          // Si pas de valeur pour ce champ, l'item ne correspond pas
                          if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                            return false;
                          }

                          const fieldValueLower = typeof fieldValue === 'string' ? fieldValue.toLowerCase().trim() : String(fieldValue).toLowerCase().trim();

                          // Vérifier si l'item correspond à AU MOINS UNE des valeurs de filtre pour ce champ (OR)
                          return filterValues.some(filterValue => {
                            // Pour les couleurs, gérer les combinaisons (ex: "noir et blanc")
                            if (field === 'color' && typeof fieldValue === 'string' && fieldValueLower.includes(' et ')) {
                              const colors = fieldValueLower.split(' et ').map(c => c.trim());
                              return colors.includes(filterValue);
                            }

                            // Comparaison exacte (insensible à la casse)
                            return fieldValueLower === filterValue;
                          });
                        });
                      });
                    }

                  // Appliquer le tri (créer une nouvelle copie pour éviter la mutation directe)
                  filteredResults = [...filteredResults].sort((a, b) => {
                    if (sortBy === 'likes') {
                      const likesA = a.likes || 0;
                      const likesB = b.likes || 0;
                      return sortOrder === 'desc' ? likesB - likesA : likesA - likesB;
                    } else {
                      const priceA = a.price || 0;
                      const priceB = b.price || 0;
                      return sortOrder === 'desc' ? priceB - priceA : priceA - priceB;
                    }
                  });

                  return filteredResults.map((result: any, idx: number) => {
                    return (
                    <div key={result.id} className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                      <a href={result.url} target="_blank" rel="noopener noreferrer">
                        {result.image_url && <img src={result.image_url} alt="" className="w-full aspect-square object-cover" />}
                        <div className="p-3">
                          <div className="flex items-baseline justify-between mb-2">
                            <div className="font-bold text-lg text-gray-900">{result.price}€</div>
                            {result.likes !== undefined && (
                              <div className="flex items-center space-x-1 text-xs text-gray-500">
                                <span>❤️</span>
                                <span>{result.likes}</span>
                              </div>
                            )}
                          </div>

                          {/* Étiquettes des attributs */}
                          <div className="flex flex-wrap gap-1 mb-2">
                            {result.brand && result.brand.trim() !== '' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                                🏷️ {result.brand}
                              </span>
                            )}
                            {result.size && result.size.trim() !== '' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                                📏 {result.size}
                              </span>
                            )}
                            {result.color && result.color.trim() !== '' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-pink-100 text-pink-800">
                                🎨 {result.color}
                              </span>
                            )}
                            {result.condition && result.condition.trim() !== '' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800">
                                💎 {result.condition}
                              </span>
                            )}
                          </div>

                          <div className="text-xs text-gray-500 truncate">{result.title}</div>
                        </div>
                      </a>
                      <button onClick={() => removeItem(result.id)} className="absolute top-2 right-2 bg-white/80 backdrop-blur-sm rounded-full p-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-all hover:text-red-500">
                        <X size={14} />
                      </button>
                    </div>
                    );
                  });
                })()}
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale des articles vendus estimés */}
      {showSoldItemsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-8 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-6 py-4 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
              <div>
                <h2 className="text-xl font-bold text-white">Articles vendus estimés</h2>
                <p className="text-violet-200 text-sm mt-0.5">{soldItemsAlertName} · présents dans le scan précédent, absents du scan actuel</p>
              </div>
              <button onClick={() => setShowSoldItemsModal(false)} className="text-white hover:bg-white/20 p-2 rounded-lg transition-all">
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto flex-1 p-6">
              {soldItemsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="animate-spin text-violet-500 mr-3" size={28} />
                  <span className="text-gray-500 text-lg">Chargement des articles...</span>
                </div>
              ) : soldItems.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-5xl mb-4">📦</p>
                  <p className="text-gray-500 text-lg font-medium">Aucun article vendu détecté</p>
                  <p className="text-gray-400 text-sm mt-2">Tous les articles du scan précédent sont encore en ligne.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
                    <span className="text-2xl font-bold text-violet-700">{soldItems.length}</span>
                    <span className="text-sm text-violet-600">article{soldItems.length > 1 ? 's' : ''} estimé{soldItems.length > 1 ? 's' : ''} vendu{soldItems.length > 1 ? 's' : ''} depuis le dernier scan</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {soldItems.map((item: any) => (
                      <a
                        key={item.id}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all hover:border-violet-300 group"
                      >
                        {item.image_url ? (
                          <img src={item.image_url} alt="" className="w-full aspect-square object-cover" />
                        ) : (
                          <div className="w-full aspect-square bg-gray-100 flex items-center justify-center text-gray-300 text-3xl">📷</div>
                        )}
                        <div className="p-3">
                          <div className="flex items-baseline justify-between mb-2">
                            <span className="font-bold text-lg text-violet-700">{item.price}€</span>
                            {item.likes > 0 && (
                              <span className="text-xs text-gray-400 flex items-center gap-0.5">❤️ {item.likes}</span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {item.brand && item.brand.trim() !== '' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">🏷️ {item.brand}</span>
                            )}
                            {item.size && item.size.trim() !== '' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">📏 {item.size}</span>
                            )}
                            {item.condition && item.condition.trim() !== '' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">💎 {item.condition}</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 truncate">{item.title}</p>
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default VintedPriceTracker;
